// ===== React + Libs =====
import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { MentionsInput, Mention } from "react-mentions";
import Cropper from "react-easy-crop";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PurchaseSuccessDialog from "../components/PurchaseSuccessDialog";
import TokenExpiredDialog from "../components/TokenExpiredDialog";
import { PRICING } from "../config/pricing";
import PosthogFeedbackTool from "../components/posthogFeedbackTool";
import DesignEditor from "../components/mediakit/DesignEditor";
import GeneratorHeader from "../components/mediakit/GeneratorHeader";

// Cover-/Design-Layouts als Rohtext ins Bundle importiert (Vite `?raw`).
// Früher wurden sie zur Laufzeit aus /templates/*.html gefetcht – das konnte je
// nach Cache/Timing scheitern und Vorschau ≠ Export verursachen. Jetzt sind sie
// fester Teil des Builds: deterministisch, kein Netzwerk, keine Race Condition.
import designHtmlDefault from "../templates/designs/default.html?raw";
import designHtmlSpotlight from "../templates/designs/spotlight.html?raw";
import designHtmlEditorial from "../templates/designs/editorial.html?raw";
import designHtmlBubbles from "../templates/designs/bubbles.html?raw";
import designHtmlFrame from "../templates/designs/frame.html?raw";
import designHtmlSplit from "../templates/designs/split.html?raw";
import designHtmlSidebar from "../templates/designs/sidebar.html?raw";
import designHtmlFullbleed from "../templates/designs/fullbleed.html?raw";
import designHtmlPoster from "../templates/designs/poster.html?raw";
import designHtmlArch from "../templates/designs/arch.html?raw";
import designHtmlAurora from "../templates/designs/aurora.html?raw";
import designHtmlDuotone from "../templates/designs/duotone.html?raw";
import designHtmlTicket from "../templates/designs/ticket.html?raw";
import designHtmlColumns from "../templates/designs/columns.html?raw";
import designHtmlPassepartout from "../templates/designs/passepartout.html?raw";
import designHtmlDiagonal from "../templates/designs/diagonal.html?raw";
import designHtmlGazette from "../templates/designs/gazette.html?raw";
import designHtmlNeon from "../templates/designs/neon.html?raw";


// ===== MUI Core (alles in EINEM Import, keine Duplikate) =====
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Rating,
  TextField,
  Typography,
  Chip,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Collapse,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
} from "@mui/material";

// ===== MUI Theme =====
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";

// ===== MUI Icons =====
import {
  CheckCircle as CheckCircleIcon,
  RestartAlt as RestartAltIcon,
  UploadFile as UploadFileIcon,
  FileDownload as FileDownloadIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  LockOutlined as LockOutlinedIcon,
  ShoppingCartCheckout as ShoppingCartCheckoutIcon,
  ExpandMore as ExpandMoreIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";


// ======= CONSTANTS / CONFIG =======
const PURPLE = "#7d9c14";
const SUCCESS = "#2e7d32";
const PRO_TOKEN = "Ee5n39TFYNSqC4";
const PRO_KEY = "mk:proToken";
const TOKEN_PREFIX = "mk_";
const TOKEN_SECRET = "mk-premium:v1:2024-10";

const xorString = (input, key) =>
  Array.from(input, (ch, i) => String.fromCharCode(ch.charCodeAt(0) ^ key.charCodeAt(i % key.length))).join("");

const base64UrlEncode = (input) =>
  btoa(input).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");

const base64UrlDecode = (input) => {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  return atob(`${padded}${pad}`);
};

const tokenChecksum = (input) => {
  let sum = 0;
  for (let i = 0; i < input.length; i += 1) {
    sum = (sum + input.charCodeAt(i) * (i + 1)) % 100000;
  }
  return sum.toString(36);
};

const decodeProToken = (token) => {
  if (!token) return { valid: false, expired: false };
  if (token === PRO_TOKEN) return { valid: true, expired: false, expiryMs: Number.POSITIVE_INFINITY, legacy: true };

  const raw = token.startsWith(TOKEN_PREFIX) ? token.slice(TOKEN_PREFIX.length) : token;
  try {
    const decoded = xorString(base64UrlDecode(raw), TOKEN_SECRET);
    const parts = decoded.split(":");
    if (parts.length < 3) return { valid: false, expired: false };

    const [expiryStr, random, sig] = parts;
    if (!expiryStr || !random || !sig) return { valid: false, expired: false };

    const expected = tokenChecksum(`${expiryStr}:${random}${TOKEN_SECRET}`);
    if (expected !== sig) return { valid: false, expired: false };

    const expiryMs = Number(expiryStr);
    if (!Number.isFinite(expiryMs)) return { valid: false, expired: false };

    const expired = Date.now() > expiryMs;
    return { valid: !expired, expired, expiryMs };
  } catch {
    return { valid: false, expired: false };
  }
};

const stripNonPrintMediaQueries = (html) =>
  html.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (full, css) => {
    const cleaned = removeNonPrintMediaQueries(css);
    return full.replace(css, cleaned);
  });

const removeNonPrintMediaQueries = (css) => {
  let out = "";
  let i = 0;
  while (i < css.length) {
    const idx = css.indexOf("@media", i);
    if (idx === -1) {
      out += css.slice(i);
      break;
    }
    out += css.slice(i, idx);
    const braceStart = css.indexOf("{", idx);
    if (braceStart === -1) {
      out += css.slice(idx);
      break;
    }
    const header = css.slice(idx, braceStart);
    let depth = 0;
    let k = braceStart;
    for (; k < css.length; k += 1) {
      const ch = css[k];
      if (ch === "{") depth += 1;
      else if (ch === "}") {
        depth -= 1;
        if (depth === 0) {
          k += 1;
          break;
        }
      }
    }
    const block = css.slice(idx, k);
    if (/print/i.test(header)) {
      out += block;
    }
    i = k;
  }
  return out;
};

// HTML templates (Layouts)
const DESIGN_TEMPLATES = [
  { id: "classic", name: "Standard", url: "/templates/default.html", preview: "/templates/default.png?v=2" },
  { id: "gradients", name: "Verläufe", url: "/templates/gradients.html", preview: "/templates/gradients.png?v=2" },
  { id: "bubbles", name: "Bubbles", url: "/templates/bubbles.html", preview: "/templates/bubbles.png?v=2" },
  { id: "minimal", name: "Minimal Split", url: "/templates/minimal.html", preview: "/templates/default.png?v=2" },
  { id: "spotlight", name: "Spotlight", url: "/templates/spotlight.html", preview: "/templates/spotlight.png?v=2" },
  { id: "editorial", name: "Editorial", url: "/templates/editorial.html", preview: "/templates/editorial.png?v=2" },
  { id: "frame", name: "Rahmen", url: "/templates/frame.html", preview: "/templates/frame.png?v=2" },
  { id: "split", name: "Split", url: "/templates/split.html", preview: "/templates/split.png?v=2" },
  { id: "sidebar", name: "Magazin", url: "/templates/sidebar.html", preview: "/templates/sidebar.png?v=2" },
  { id: "fullbleed", name: "Vollbild", url: "/templates/fullbleed.html", preview: "/templates/default.png?v=2" },
  { id: "poster", name: "Poster", url: "/templates/poster.html", preview: "/templates/poster.png?v=2" },
  { id: "arch", name: "Bogen", url: "/templates/arch.html", preview: "/templates/arch.png?v=2" },
  { id: "aurora", name: "Aurora", url: "/templates/aurora.html", preview: "/templates/aurora.png?v=2" },
  { id: "duotone", name: "Duotone", url: "/templates/duotone.html", preview: "/templates/duotone.png?v=2" },
  { id: "ticket", name: "Ticket", url: "/templates/ticket.html", preview: "/templates/ticket.png?v=2" },
  { id: "columns", name: "Kolumnen", url: "/templates/columns.html", preview: "/templates/columns.png?v=2" },
  { id: "passepartout", name: "Passepartout", url: "/templates/passepartout.html", preview: "/templates/passepartout.png?v=2" },
  { id: "diagonal", name: "Diagonale", url: "/templates/diagonal.html", preview: "/templates/diagonal.png?v=2" },
  { id: "gazette", name: "Gazette", url: "/templates/gazette.html", preview: "/templates/gazette.png?v=2" },
  { id: "neon", name: "Neon", url: "/templates/neon.html", preview: "/templates/neon.png?v=2" },
];
// Design-Layout-HTML direkt aus dem Bundle (siehe ?raw-Imports oben).
// Nur diese IDs haben ein echtes Layout; "minimal"/"gradients" sind versteckt
// und ohne Datei -> resolveDesignId fällt bei ihnen auf den Default zurück.
const DESIGN_TEMPLATE_HTML = {
  classic: designHtmlDefault,
  spotlight: designHtmlSpotlight,
  editorial: designHtmlEditorial,
  bubbles: designHtmlBubbles,
  frame: designHtmlFrame,
  split: designHtmlSplit,
  sidebar: designHtmlSidebar,
  fullbleed: designHtmlFullbleed,
  poster: designHtmlPoster,
  arch: designHtmlArch,
  aurora: designHtmlAurora,
  duotone: designHtmlDuotone,
  ticket: designHtmlTicket,
  columns: designHtmlColumns,
  passepartout: designHtmlPassepartout,
  diagonal: designHtmlDiagonal,
  gazette: designHtmlGazette,
  neon: designHtmlNeon,
};
const HIDDEN_DESIGN_IDS = new Set(["minimal", "gradients", "bubbles", "classic", "fullbleed"]);
const SELECTABLE_DESIGN_TEMPLATES = DESIGN_TEMPLATES.filter((design) => !HIDDEN_DESIGN_IDS.has(design.id));
const DEFAULT_DESIGN_ID = "spotlight";
const FALLBACK_DESIGN_ID =
  DESIGN_TEMPLATES.find((design) => design.id === DEFAULT_DESIGN_ID)?.id || DESIGN_TEMPLATES[0]?.id || "";
const resolveDesignId = (candidate) => {
  if (typeof candidate !== "string") return FALLBACK_DESIGN_ID;
  const trimmed = candidate.trim();
  if (!trimmed) return FALLBACK_DESIGN_ID;
  const directMatch = DESIGN_TEMPLATES.find((design) => design.id === trimmed);
  if (directMatch) return directMatch.id;
  const urlMatch = DESIGN_TEMPLATES.find((design) => design.url === trimmed);
  if (urlMatch) return urlMatch.id;
  const filename = trimmed.split("/").pop();
  if (filename) {
    const filenameMatch = DESIGN_TEMPLATES.find((design) => design.url.split("/").pop() === filename);
    if (filenameMatch) return filenameMatch.id;
  }
  return FALLBACK_DESIGN_ID;
};
const NAME_SHADOW_PRESETS = {
  classic: "0 4px 22px",
  gradients: "0 4px 22px",
  bubbles: "0 10px 30px",
  minimal: "0 4px 18px",
  spotlight: "0 16px 50px",
};
const NAME_COLOR_KEYS = new Set(["c1", "c2", "c3"]);


const HERO_ASPECT = 1 / 1;
const PORTRAIT_ASPECT = 3 / 4;

const STORAGE_KEY = "mk:autosave:v1"; // beibehaltener Key für Rückwärtskompatibilität
const MOCKUP_IMG_HERO = "/img.jpg";
const MOCKUP_IMG_FOOTER = "/mockup/page1_footer.png";
const MOCKUP_IMG_PAGE2 = "/sirus-portrait.jpeg";

const A4_RATIO = 297 / 210;

const COLOR_TEMPLATES = [
  { id: "ocean", name: "Ocean", c1: "#0ea5e9", c2: "#111827", c3: "#e6f6fe", on1: "#ffffff", on2: "#ffffff", on3: "#0b1220" },
  { id: "forest", name: "Forest", c1: "#10b981", c2: "#064e3b", c3: "rgba(194, 255, 196, 1)", on1: "#0b1f16", on2: "#eafff6", on3: "#083f2f" },
  { id: "violet", name: "Violet", c1: "#8b5cf6", c2: "#1f2937", c3: "#efe9ff", on1: "#ffffff", on2: "#e5e7eb", on3: "#261b4a" },
  { id: "slate", name: "Slate", c1: "#111827", c2: "#6b7280", c3: "#f3f4f6", on1: "#ffffff", on2: "#ffffff", on3: "#111827" },
  { id: "ember", name: "Ember", c1: "#f97316", c2: "#1f2937", c3: "#fff3e6", on1: "#111111", on2: "#ffffff", on3: "#111111" },
  { id: "gold", name: "Gold", c1: "#d97706", c2: "#0f172a", c3: "#fff7e6", on1: "#111111", on2: "#ffffff", on3: "#111111" },
  { id: "mono", name: "Mono", c1: "#111827", c2: "#374151", c3: "#f3f4f6", on1: "#ffffff", on2: "#ffffff", on3: "#111827" },
  { id: "teal", name: "Teal", c1: "#14b8a6", c2: "#0f172a", c3: "#e7f7f5", on1: "#07231f", on2: "#e5e7eb", on3: "#0b1220" },
  { id: "blush", name: "Blush", c1: "#f43f5e", c2: "#1f2937", c3: "#ffe4ea", on1: "#ffffff", on2: "#e5e7eb", on3: "#6b0f1a" },
  { id: "pastel-rose", name: "Pastel Rose", c1: "#f9a8d4", c2: "#6b4b5a", c3: "#fff1f7", on1: "#4b2333", on2: "#ffffff", on3: "#4b2333" },
  { id: "indigo", name: "Indigo", c1: "#4f46e5", c2: "#111827", c3: "#eef2ff", on1: "#ffffff", on2: "#ffffff", on3: "#111827" },
  { id: "copper", name: "Copper", c1: "#b45309", c2: "#1f2937", c3: "#fff4e6", on1: "#ffffff", on2: "#e5e7eb", on3: "#111827" },
  { id: "berry", name: "Berry", c1: "#a21caf", c2: "#3b0a2a", c3: "#fde7ff", on1: "#ffffff", on2: "#ffffff", on3: "#3b0a2a" },
];
const SPOTLIGHT_HIDDEN_COLOR_IDS = new Set(["slate", "mono"]);

// Section-Layouts direkt im Code statt als /templates/sections/*.html.
// Grund: der frühere Runtime-fetch konnte je nach Timing/Cache scheitern oder
// erst nach dem Messen ankommen -> Vorschau und Export liefen auseinander.
// Inline-Strings sind deterministisch und Teil des Bundles (kein Netzwerk).
// Platzhalter: {{sectionTitle}}, {{sectionBody}}, {{sectionImageUrl}}.
const SIMPLE_SECTION_TEMPLATE = `<section class="section-shell">
  <div class="section-block">
    <h2 class="section-title">{{sectionTitle}}</h2>
    <div class="section-body">{{sectionBody}}</div>
  </div>
</section>`;
const SECTION_TEMPLATES = {
  bio: `<section class="section-shell section-shell--bio">
  <div class="section-block section-block--bio">
    <h2 class="section-title">{{sectionTitle}}</h2>
    <div class="section-body-wrap section-body-wrap--bio">
      <div class="section-body section-body--bio">{{sectionBody}}</div>
      <img class="section-image section-image--bio" src="{{sectionImageUrl}}" alt="" />
    </div>
  </div>
</section>`,
  angebot: SIMPLE_SECTION_TEMPLATE,
  repertoire: SIMPLE_SECTION_TEMPLATE,
  rider: SIMPLE_SECTION_TEMPLATE,
};

const SECTION_LABELS = {
  bio: "Bio",
  angebot: "Angebot",
  repertoire: "Repertoire",
  rider: "Tech Rider",
  pressMaterial: "Pressematerial",
  contact: "Kontakt",
};

const SECTION_COLOR_MODES = [
  { id: "c1", label: "Akzent" },
  { id: "c2", label: "Dunkel" },
  { id: "c3", label: "Hell" },
];

const SECTION_COLOR_CYCLE = ["c1", "c2", "c3"];
const buildDefaultSectionModes = () => {
  return Object.keys(SECTION_LABELS).reduce((acc, key, index) => {
    acc[key] = SECTION_COLOR_CYCLE[index % SECTION_COLOR_CYCLE.length];
    return acc;
  }, {});
};

const SECTION_KEYS = Object.keys(SECTION_LABELS);
const DEFAULT_SECTION_ORDER = [...SECTION_KEYS];
const buildDefaultSectionPadding = () =>
  SECTION_KEYS.reduce((acc, key) => {
    acc[key] = { top: "auto", bottom: "auto" };
    return acc;
  }, {});
// Bringt eine (evtl. unvollständige) Reihenfolge auf alle Sektionen, Reste hinten angehängt.
const normalizeSectionOrder = (order) => {
  const valid = Array.isArray(order) ? order.filter((k) => SECTION_KEYS.includes(k)) : [];
  const seen = new Set();
  const deduped = valid.filter((k) => (seen.has(k) ? false : seen.add(k)));
  const missing = SECTION_KEYS.filter((k) => !seen.has(k));
  return [...deduped, ...missing];
};
// "auto" oder Zahl >= 0
const sanitizePadValue = (v) => {
  if (v === "auto") return "auto";
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : "auto";
};
const normalizeSectionPadding = (padding) => {
  const base = buildDefaultSectionPadding();
  if (padding && typeof padding === "object") {
    SECTION_KEYS.forEach((key) => {
      const entry = padding[key];
      if (entry && typeof entry === "object") {
        base[key] = {
          top: sanitizePadValue(entry.top),
          bottom: sanitizePadValue(entry.bottom),
        };
      }
    });
  }
  return base;
};
const SECTION_TEXT_COLOR_KEYS = new Set(["auto", "c1", "c2", "c3"]);

// Höhe der Übergangs-Grafik zwischen zwei Sektionen.
const DIVIDER_H = 34;
const DIVIDER_STYLE_KEYS = new Set(["none", "angle-right", "angle-left", "wave", "zigzag", "scallop", "gradient"]);

// Zeichnet den Übergang zwischen zwei farbigen Bändern auf ein Canvas und gibt
// ein PNG-DataURL zurück. Bewusst als gerastertes Bild (nicht als SVG/clip-path),
// damit Vorschau und PDF über html2canvas pixelgleich rendern.
function makeDividerImage(style, topColor, bottomColor, width = 794, height = DIVIDER_H, dpr = 2) {
  const canvas = document.createElement("canvas");
  const w = Math.max(1, Math.round(width * dpr));
  const h = Math.max(1, Math.round(height * dpr));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  if (style === "gradient") {
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, topColor);
    grad.addColorStop(1, bottomColor);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    return canvas.toDataURL("image/png");
  }

  // Grundfläche = obere Farbe, untere Farbe als Form daraufzeichnen.
  ctx.fillStyle = topColor;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = bottomColor;
  ctx.beginPath();
  if (style === "angle-right") {
    ctx.moveTo(0, h);
    ctx.lineTo(w, 0);
    ctx.lineTo(w, h);
    ctx.closePath();
  } else if (style === "angle-left") {
    ctx.moveTo(0, 0);
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
  } else if (style === "wave") {
    const amp = h * 0.42;
    const mid = h * 0.5;
    const steps = 64;
    ctx.moveTo(0, mid);
    for (let i = 0; i <= steps; i += 1) {
      const x = (w * i) / steps;
      const y = mid - amp * Math.sin((i / steps) * Math.PI * 2);
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
  } else {
    // Fallback: harte Kante in der Mitte.
    ctx.rect(0, Math.round(h / 2), w, h);
  }
  ctx.fill();
  return canvas.toDataURL("image/png");
}

// ===== Version A: Per-Trenner-Konfiguration (Typ + Abstand) =====
// Jeder Trenner sitzt zwischen zwei aufeinanderfolgenden Sektionen und wird über
// den Schlüssel der DARÜBER liegenden Sektion adressiert (afterKey). type steuert
// die Übergangsgrafik, spacing die Höhe der Lücke (px). Beides ist direkt in der
// Vorschau per Hover am Trenner einstellbar.
const DIVIDER_TYPE_KEYS = new Set(["none", "angle-right", "angle-left", "wave", "zigzag", "scallop", "gradient"]);
const DIVIDER_TYPE_OPTIONS = [
  { id: "none", label: "Kein" },
  { id: "angle-right", label: "Schräge rechts" },
  { id: "angle-left", label: "Schräge links" },
  { id: "wave", label: "Welle" },
  { id: "zigzag", label: "Zickzack" },
  { id: "scallop", label: "Bögen" },
  { id: "gradient", label: "Verlauf" },
];
const DEFAULT_DIVIDER_TYPE = "none";
const DEFAULT_DIVIDER_SPACING = 26;
const DIVIDER_SPACING_MIN = 6;
const DIVIDER_SPACING_MAX = 160;
const DIVIDER_SPACING_STEP = 14;
const clampDividerSpacing = (v) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return DEFAULT_DIVIDER_SPACING;
  return Math.max(DIVIDER_SPACING_MIN, Math.min(DIVIDER_SPACING_MAX, n));
};
// Liefert eine garantiert gültige Trenner-Konfiguration für einen afterKey.
const resolveDividerConfig = (cfg, key) => {
  const entry = cfg && typeof cfg === "object" ? cfg[key] : null;
  const type = entry && DIVIDER_TYPE_KEYS.has(entry.type) ? entry.type : DEFAULT_DIVIDER_TYPE;
  const spacing = clampDividerSpacing(entry ? entry.spacing : DEFAULT_DIVIDER_SPACING);
  return { type, spacing };
};
// Bereinigt eine evtl. aus Autosave/Template geladene Trenner-Map.
const normalizeDividerConfig = (cfg) => {
  const out = {};
  if (cfg && typeof cfg === "object") {
    Object.keys(cfg).forEach((key) => {
      if (!SECTION_KEYS.includes(key)) return;
      out[key] = resolveDividerConfig(cfg, key);
    });
  }
  return out;
};

// Wellenpfad in 0..100-Koordinaten (preserveAspectRatio=none, skaliert mit der
// Trennerhöhe). Bewusst als Pfad, damit Vorschau und PDF identisch rendern.
function dividerWavePath() {
  const amp = 24;
  const mid = 52;
  const steps = 48;
  let d = `M0,${mid}`;
  for (let i = 0; i <= steps; i += 1) {
    const x = (100 * i) / steps;
    const y = mid - amp * Math.sin((i / steps) * Math.PI * 2);
    d += ` L${x.toFixed(2)},${y.toFixed(2)}`;
  }
  d += " L100,100 L0,100 Z";
  return d;
}
// --- Zierleisten-Kacheln -------------------------------------------------
// Die Muster-Trenner (Zickzack, Bögen) werden NICHT über die volle Breite
// gestreckt (das ergäbe wenige, riesige Zacken), sondern als kleine Kachel
// horizontal gewiederholt (background-repeat:repeat-x). So bleibt das Muster
// gleichmässig fein – eine echte Zierleiste – unabhängig von der Seitenbreite.
// Jede Kachel ist ein feiner Zierstreifen: schmale Akzentbänder oben/unten
// rahmen das eigentliche Motiv, das mittig zwischen den beiden Bandfarben sitzt.

// Zickzack-Kachel (2 Zacken) im 0..100-Viewport, wird schmal gekachelt.
function dividerZigzagTile() {
  return "M0,70 L25,30 L50,70 L75,30 L100,70 L100,100 L0,100 Z";
}
// Bogen-Kachel (2 Halbkreise) – klassische Muschelkante.
function dividerScallopTile() {
  return "M0,64 A25,25 0 0 0 50,64 A25,25 0 0 0 100,64 L100,100 L0,100 Z";
}
// Baut eine gekachelte Zierleiste: feine Akzentlinie oben, Motiv in der Farbe
// des unteren Bandes, dünne Grundlinie unten – als wiederholbare SVG-Kachel.
function dividerOrnamentTile(path, topColor, bottomColor) {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'>` +
    `<rect width='100' height='100' fill='${topColor}'/>` +
    `<path d='${path}' fill='${bottomColor}'/>` +
    // feine Grundlinie in der Akzentfarbe für den „Leisten“-Charakter
    `<rect x='0' y='96' width='100' height='4' fill='${bottomColor}'/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
// CSS-Layout für einen Trenner. Liefert background + Größe/Wiederholung, damit
// gestreckte Kanten (Schräge/Welle) und gekachelte Zierleisten (Zickzack/Bögen)
// jeweils korrekt rendern – pixelgleich in Vorschau (Iframe) und Server-PDF.
function dividerBackground(type, topColor, bottomColor) {
  if (type === "gradient") {
    return {
      background: `linear-gradient(to bottom, ${topColor}, ${bottomColor})`,
      size: "100% 100%",
      repeat: "no-repeat",
      position: "center",
    };
  }
  // Gekachelte Zierleisten: kleine Kachel, horizontal wiederholt.
  if (type === "zigzag" || type === "scallop") {
    const path = type === "zigzag" ? dividerZigzagTile() : dividerScallopTile();
    const url = dividerOrnamentTile(path, topColor, bottomColor);
    const tileW = type === "zigzag" ? 40 : 46;
    return {
      background: `${topColor} url("${url}")`,
      size: `${tileW}px 100%`,
      repeat: "repeat-x",
      position: "center",
    };
  }
  // Gestreckte Einzelkanten (Schräge, Welle).
  let path;
  if (type === "angle-right") path = "M0,100 L100,0 L100,100 Z";
  else if (type === "angle-left") path = "M0,0 L100,100 L0,100 Z";
  else if (type === "wave") path = dividerWavePath();
  else path = "M0,50 L100,50 L100,100 L0,100 Z"; // none -> harte Kante mittig
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'>` +
    `<rect width='100' height='100' fill='${topColor}'/>` +
    `<path d='${path}' fill='${bottomColor}'/></svg>`;
  return {
    background: `${topColor} url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    size: "100% 100%",
    repeat: "no-repeat",
    position: "center",
  };
}

// WICHTIG für "Preview == PDF": Jede Option MUSS als primäre Schrift einen echten
// Webfont haben, der in PDF_FONT_STYLESHEET geladen wird. Plattform-Schriften
// (system-ui, Georgia, Courier New) existieren auf macOS, aber nicht identisch auf
// Api2Pdfs Linux-Chrome -> unterschiedliche Metriken -> Preview und Export driften.
// Deshalb steht überall ein geladener Webfont VORNE; Systemschriften nur als Notnagel.
const FONT_OPTIONS = [
  {
    id: "system",
    label: "Clean Sans",
    css: '"Montserrat", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: "modern",
    label: "Modern Sans",
    css: '"Roboto", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: "serif",
    label: "Serif Klassisch",
    css: '"Lora", Georgia, "Times New Roman", serif',
  },

  {
    id: "softSans",
    label: "Soft Sans",
    css: '"Nunito", system-ui, -apple-system, "Segoe UI", sans-serif',
  },
  {
    id: "humanSans",
    label: "Human Sans",
    css: '"Source Sans 3", system-ui, -apple-system, "Segoe UI", sans-serif',
  },
  {
    id: "elegantSerif",
    label: "Elegant Serif",
    css: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
  },
  {
    id: "editorial",
    label: "Editorial Serif",
    css: '"Lora", Georgia, "Times New Roman", serif',
  },
  {
    id: "softScript",
    label: "Signature",
    css: '"Dancing Script", "Segoe Script", "Brush Script MT", cursive',
  },
  {
    id: "parisienne",
    label: "Parisienne",
    css: '"Parisienne", "Segoe Script", "Brush Script MT", cursive',
  },
  {
    id: "allura",
    label: "Allura",
    css: '"Allura", "Segoe Script", "Brush Script MT", cursive',
  },
  {
    id: "greatVibes",
    label: "Great Vibes",
    css: '"Great Vibes", "Segoe Script", "Brush Script MT", cursive',
  },
  {
    id: "handClean",
    label: "Handschrift Clean",
    css: '"Caveat", "Segoe Script", cursive',
  },

  {
    id: "display",
    label: "Display",
    css: '"Playfair Display", "Times New Roman", serif',
  },

  {
    id: "mono",
    label: "Mono",
    css: '"Courier New", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
];

// Nur sehr gut lesbare Schriften für die Textblöcke (Fließtext).
const BODY_FONT_OPTIONS = FONT_OPTIONS.filter((f) =>
  ["system", "modern", "softSans", "humanSans", "serif"].includes(f.id),
);

// Standardisierte "Komponenten"-Maße für ALLE Titel-Templates. Eine einzige
// Quelle der Wahrheit: hier ändern -> überall gleich (QR, CTA-Button, Kicker,
// Titelfoto). Die Templates referenzieren diese als CSS-Variablen mit Fallback,
// damit sie auch standalone sinnvoll aussehen.
const TEMPLATE_TOKENS = {
  "--qr-size": "104px",
  "--qr-pad": "7px",
  "--qr-radius": "14px",
  "--cta-pad": "13px 22px",
  "--cta-radius": "999px",
  "--kicker-pad": "6px 16px",
  "--kicker-size": "11px",
  "--kicker-tracking": "0.34em",
  "--accent-bar-height": "6px",
  "--cta-link-max": "270px",
  // Titelfoto (Kreis-Avatar) – deutlich größer als zuvor. Editorial nutzt ein
  // eigenes Foto-Band und ignoriert diesen Token.
  "--hero-photo-size": "440px",
};

// Minimalistische, rein dekorative Motive für die INHALTSSEITEN (Sequenzseiten).
// Nur ein zartes Rand-Overlay pro Design – NIE im Textfluss, nie layoutverschiebend:
// `.page-sections::after` liegt als absolute Ebene (pointer-events:none) an der
// Blattkante über den (deckenden) Bändern. Nur kuratierte Designs bekommen eins;
// alle anderen bleiben bewusst schlicht. CSS-only -> im PDF scharf & leichtgewichtig.
// Hinweise:
//  - Bottom endet bei 52px -> IMMER oberhalb der Fußleiste (Footer bleibt frei).
//  - Linienfarbe ist neutral (dunkel) MIT weißem 1px-Ring (box-shadow): dadurch
//    bleibt die Linie auf hellen, dunklen UND akzentfarbenen Bändern sichtbar –
//    sie verschwindet nie, auch wenn ein Segment dieselbe Farbe hätte.
const SECTION_DECOR = {
  // Rahmen: feine Linie ums Blatt (endet über dem Footer)
  frame:
    ".page-sections::after{content:'';position:absolute;top:16px;left:16px;right:16px;bottom:52px;pointer-events:none;border:1px solid rgba(15,23,42,0.34);box-shadow:0 0 0 1px rgba(255,255,255,0.5);}",
  // Passepartout: doppelte Mat-Linie
  passepartout:
    ".page-sections::after{content:'';position:absolute;top:17px;left:17px;right:17px;bottom:52px;pointer-events:none;border:1px solid rgba(15,23,42,0.32);outline:1px solid rgba(15,23,42,0.32);outline-offset:4px;box-shadow:0 0 0 1px rgba(255,255,255,0.5);}",
  // Ticket: perforierte, gerundete Kante
  ticket:
    ".page-sections::after{content:'';position:absolute;top:16px;left:16px;right:16px;bottom:52px;pointer-events:none;border:1.5px dashed rgba(15,23,42,0.36);border-radius:12px;box-shadow:0 0 0 1px rgba(255,255,255,0.45);}",
  // Gazette: klassischer Doppellinien-Rahmen (Masthead-Anmutung)
  gazette:
    ".page-sections::after{content:'';position:absolute;top:20px;left:34px;right:34px;bottom:52px;pointer-events:none;border:3px double rgba(15,23,42,0.36);box-shadow:0 0 0 1px rgba(255,255,255,0.45);}",
  // Neon: dünner leuchtender Rahmen (behält Farbe, plus weißer Ring als Sicherung)
  neon:
    ".page-sections::after{content:'';position:absolute;top:16px;left:16px;right:16px;bottom:52px;pointer-events:none;border:1.5px solid var(--c1);border-radius:8px;box-shadow:0 0 12px var(--c1),inset 0 0 12px rgba(56,189,248,0.15),0 0 0 1px rgba(255,255,255,0.28);}",
  // Editorial: schmaler Akzentbalken oben (mit dunkler Trennkante, damit er nie verschwindet)
  editorial:
    ".page-sections::after{content:'';position:absolute;left:0;right:0;top:0;height:5px;pointer-events:none;background:var(--c1);box-shadow:0 1px 0 rgba(15,23,42,0.2);}",
  // Kolumnen: Akzentschiene am linken Rand (endet über dem Footer)
  columns:
    ".page-sections::after{content:'';position:absolute;left:0;top:0;bottom:52px;width:5px;pointer-events:none;background:var(--c1);box-shadow:1px 0 0 rgba(15,23,42,0.2);}",
};

const TITLE_FONT =
  FONT_OPTIONS.find((f) => f.id === "modern")?.css ||
  FONT_OPTIONS[0].css;

// Google-Fonts-Stylesheet mit ALLEN tatsächlich verwendeten Schriftfamilien.
// Wird sowohl für die serverseitige PDF-Erzeugung (Api2Pdf-Chrome) als auch –
// über index.html – für die Vorschau geladen, damit Vorschau und PDF dieselben
// echten Schriften nutzen und auf jedem Browser identisch aussehen.
const PDF_FONT_STYLESHEET =
  "https://fonts.googleapis.com/css2?" +
  [
    "family=Roboto:wght@400;500;700;800",
    "family=Nunito:wght@400;600;700;800",
    "family=Source+Sans+3:wght@400;600;700",
    "family=Cormorant+Garamond:wght@400;600;700",
    "family=Lora:wght@400;500;600;700",
    "family=Dancing+Script:wght@400;700",
    "family=Parisienne",
    "family=Allura",
    "family=Great+Vibes",
    "family=Caveat:wght@400;700",
    "family=Playfair+Display:wght@400;600;700;800;900",
    "family=Bodoni+Moda:wght@400;500;700;800;900",
    "family=Space+Grotesk:wght@400;500;600;700",
    "family=Montserrat:wght@400;500;600;700;800",
  ].join("&") +
  // display=block: im PDF lieber kurz auf die echten Fonts warten als Fallback drucken.
  "&display=block";

// Baut aus dem fertig gerenderten (offscreen) Host-Element ein vollständig
// eigenständiges HTML-Dokument für die serverseitige PDF-Erzeugung.
// - übernimmt alle CSS-Custom-Properties vom Host (Farben, Schriftgrößen, …)
// - bindet die Web-Fonts ein
// - setzt @page exakt auf unsere Seitengröße (794x1123px = A4 @96dpi)
// Bilder/QR-Codes sind bereits als data:-URLs eingebettet → komplett self-contained.
function buildStandalonePdfHtml(host) {
  let varCss = "";
  const style = host.style;
  for (let i = 0; i < style.length; i++) {
    const prop = style[i];
    if (prop.startsWith("--")) {
      varCss += `${prop}:${style.getPropertyValue(prop)};`;
    }
  }
  const inner = host.innerHTML;
  // WICHTIG: Schrift-Variablen enthalten doppelte Anführungszeichen (z. B.
  // "Space Grotesk"). Unmaskiert würden sie das style="..."-Attribut vorzeitig
  // beenden und ALLE danach gesetzten Variablen (Schriftgrößen, Zeilenabstand,
  // Titel-Schrift …) verschlucken. Für den Attribut-Kontext escapen.
  const varAttr = varCss.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${PDF_FONT_STYLESHEET}" rel="stylesheet">
<style>
@page{ size:794px 1123px; margin:0; }
html,body{ margin:0; padding:0; background:#fff; }
html,body,*{ -webkit-print-color-adjust:exact; print-color-adjust:exact; }
/* Ohne Upload bleibt src leer -> kein leeres/kaputtes Bild, kein Alt-Text im PDF. */
img[src=""],img:not([src]){ display:none !important; }
</style>
</head>
<body><div style="width:794px;font-family:var(--font-family-body);${varAttr}">${inner}</div></body>
</html>`;
}

// Caches: bei jedem Vorschau-Rebuild (passiert bei jedem Tastendruck) NICHT
// dieselbe Arbeit wiederholen. Templates ändern sich nie zur Laufzeit, QR-Codes
// nur wenn sich die URL ändert.
const _templateCache = new Map(); // designId -> bereits bereinigtes HTML
const _qrCache = new Map(); // url -> data-URL

async function cachedQrDataUrl(url) {
  if (!url) return "";
  if (_qrCache.has(url)) return _qrCache.get(url);
  const data = await QRCode.toDataURL(url, {
    errorCorrectionLevel: "M",
    margin: 1,
    scale: 6,
  });
  _qrCache.set(url, data);
  return data;
}

// Wie buildStandalonePdfHtml, aber nur EINE Seite – für die Live-Vorschau im
// Iframe. Nutzt exakt dieselbe CSS-Quelle (alle <style>-Blöcke + CSS-Variablen
// des Hosts) wie das finale PDF, daher: Vorschau == PDF, ganz ohne html2canvas.
function buildPagePreviewHtml(host, pageEl) {
  let varCss = "";
  const style = host.style;
  for (let i = 0; i < style.length; i++) {
    const prop = style[i];
    if (prop.startsWith("--")) {
      varCss += `${prop}:${style.getPropertyValue(prop)};`;
    }
  }
  // Siehe buildStandalonePdfHtml: Schrift-Variablen enthalten Anführungszeichen
  // und müssen für den style="..."-Attribut-Kontext maskiert werden, sonst gehen
  // alle nachfolgenden Variablen (Schriftgrößen, Zeilenabstand …) verloren.
  const varAttr = varCss.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  let styleCss = "";
  host.querySelectorAll("style").forEach((s) => {
    styleCss += s.textContent + "\n";
  });
  return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${PDF_FONT_STYLESHEET}" rel="stylesheet">
<style>
html,body{ margin:0; padding:0; background:#fff; }
html,body,*{ -webkit-print-color-adjust:exact; print-color-adjust:exact; }
img[src=""],img:not([src]){ display:none !important; }
${styleCss}
</style>
</head>
<body><div style="width:794px;font-family:var(--font-family-body);${varAttr}">${pageEl.outerHTML}</div></body>
</html>`;
}

const DEFAULT_TEMPLATE_ID = COLOR_TEMPLATES[0]?.id || "";
const DEFAULT_CUSTOM_COLORS = { c1: "#0ea5e9", c2: "#111827", c3: "#e6f6fe", on1: "#ffffff", on2: "#ffffff", on3: "#0b1220" };
// WICHTIG: kein "system" als Default. Plattform-Schriften (macOS vs. Api2Pdf-Linux)
// haben unterschiedliche Metriken -> Preview misst mit einer anderen Schrift als der
// Server rendert -> Umbrüche/Seitenumbrüche driften. Ein echter Webfont (Roboto) wird
// in BEIDEN Umgebungen aus derselben Quelle geladen und ist damit deterministisch.
const DEFAULT_FONT_ID = "modern";
const DEFAULT_NAME_FONT_ID = "modern";
const DEFAULT_TITLE_FONT_ID = "modern";
const DEFAULT_NAME_FONT_SIZE = 70;
const DEFAULT_NAME_COLOR_KEY = "c1";
const DEFAULT_NAME_SHADOW_ENABLED = true;
const DEFAULT_NAME_SHADOW_COLOR_KEY = "c2";
const DEFAULT_NAME_OUTLINE_ENABLED = false;
const DEFAULT_NAME_OUTLINE_COLOR_KEY = "c2";
const DEFAULT_TITLE_FONT_SIZE = 32;
const DEFAULT_BODY_FONT_SIZE = 13.5;

// Globale Zeilenabstand-Modi (im Preview umschaltbar). Bewusst nur wenige,
// damit es "deppensicher" bleibt und immer gut aussieht.
const LINE_SPACING_OPTIONS = [
  { id: "tight", label: "Wenig", value: 1.4 },
  { id: "normal", label: "Normal", value: 1.65 },
  { id: "loose", label: "Weit", value: 1.95 },
];
const DEFAULT_LINE_SPACING = "normal";

// Schriftgrößen des Textblocks. Quelle für Editor-Buttons UND die Overflow-
// Messung (welche Größen passen noch auf die Seite?) — muss deckungsgleich sein.
const BODY_SIZE_OPTIONS = [
  { id: "small", label: "Klein", value: 12 },
  { id: "medium", label: "Mittel", value: 13.5 },
  { id: "large", label: "Groß", value: 15 },
];

// Portrait-Bild (Bio) – im Preview per +/- skalierbar. Basisbreite 260px.
const BIO_IMAGE_BASE_WIDTH = 260;
const DEFAULT_BIO_IMAGE_SCALE = 1;
const clampBioImageScale = (v) => Math.max(0.5, Math.min(1.6, Math.round(v * 10) / 10));
const LINE_SPACING_IDS = new Set(LINE_SPACING_OPTIONS.map((o) => o.id));
const normalizeLineSpacing = (value) => (LINE_SPACING_IDS.has(value) ? value : DEFAULT_LINE_SPACING);
const lineSpacingValue = (id) =>
  (LINE_SPACING_OPTIONS.find((o) => o.id === normalizeLineSpacing(id)) || LINE_SPACING_OPTIONS[1]).value;


const GENRES = [
  "Pop",
  "Rock",
  "Jazz",
  "Electronic",
  "Hip-Hop",
  "Classical",
  "Singer-Songwriter",
  "Indie",
  "R&B / Soul",
  "Country",
  "EDM",
  "House / Techno",
  "Metal",
  "Folk",
  "Andere",
];

// ======= UTILS =======

// Liefert IDs OHNE geschweifte Klammern, z.B. "Name", "Genre/Mix: z. B. ..."
function extractPlaceholderIds(s) {
  if (!s) return [];
  const re = /\{([^}]+)\}/g;
  const set = new Set();
  let m;
  while ((m = re.exec(s))) {
    const inside = m[1].trim(); // Inhalt OHNE {}
    if (inside) set.add(inside);
  }
  return Array.from(set);
}

// Für Validierung reicht: gibt es irgendwo {…}?
function hasPlaceholders(s) {
  return /\{[^}]+\}/.test(s || "");
}

// Aus einem Text mit {Platzhaltern} ein data[] für react-mentions bauen
function buildMentionDataFromTemplate(template) {
  return extractPlaceholderIds(template).map((rawId) => {
    // optional: alles vor dem ersten ":" als kürzeres Label
    const [short] = rawId.split(":");
    const display = (short || rawId).trim();
    return {
      id: rawId,   // ergibt mit markup "{__id__}" dann {Name}, {Genre/Mix: …}
      display,     // Label in der Vorschlagsliste
    };
  });
}

function splitLegacyContact(raw) {
  const lines = (raw || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length >= 3) {
    return {
      email: lines[1] || "",
      phone: lines[2] || "",
      socialTags: lines.slice(3).join("\n"),
    };
  }
  return { email: "", phone: "", socialTags: raw || "" };
}


function isIOS() {
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}
function slugify(s) {
  return (s || "media-kit")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/(^-|-$)/g, "");
}
function escapeHtml(str) {
  return (str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
function toRgba(color, alpha) {
  if (!color) return `rgba(0,0,0,${alpha})`;
  const trimmed = color.trim();
  if (trimmed.startsWith("rgba")) {
    const parts = trimmed.replace(/rgba|\(|\)/g, "").split(",").map((p) => p.trim());
    if (parts.length >= 3) return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  }
  if (trimmed.startsWith("rgb(")) {
    const parts = trimmed.replace(/rgb|\(|\)/g, "").split(",").map((p) => p.trim());
    if (parts.length >= 3) return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  }
  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return trimmed;
}
// ===== Farb-Kontrast (WCAG) – sorgt dafür, dass Text immer lesbar bleibt =====
function parseColorToRgb(color) {
  if (!color) return null;
  const trimmed = String(color).trim();
  let m = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(",").map((p) => parseFloat(p.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) {
      return { r: parts[0], g: parts[1], b: parts[2] };
    }
    return null;
  }
  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (hex.length !== 6) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return { r, g, b };
  }
  return null;
}
function relativeLuminance(color) {
  const rgb = parseColorToRgb(color);
  if (!rgb) return 0;
  const lin = [rgb.r, rgb.g, rgb.b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}
function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}
// Liefert schwarz oder weiß – je nachdem, was auf bg besser lesbar ist.
function autoTextColor(bg) {
  return contrastRatio("#ffffff", bg) >= contrastRatio("#111111", bg) ? "#ffffff" : "#111111";
}
// Gibt wanted zurück, wenn es lesbar genug ist (>= min), sonst die beste
// schwarz/weiß-Alternative. So kann nie unsichtbarer Text entstehen.
function ensureReadable(wanted, bg, min = 4.5) {
  if (wanted && contrastRatio(wanted, bg) >= min) return wanted;
  return autoTextColor(bg);
}
function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}
async function getCroppedImg(imageSrc, cropPixels, type = "image/jpeg") {
  const image = await createImage(imageSrc);
  // Zuschnitt auf sinnvolle Maße begrenzen: sehr große Uploads würden sonst als
  // riesige data-URL im State/localStorage UND im PDF landen -> träge & sprengt
  // Größenlimits. 1400px lange Kante @0.9 ist für Print (A4) mehr als genug.
  const MAX_DIM = 1400;
  const scale = Math.min(1, MAX_DIM / Math.max(cropPixels.width, cropPixels.height));
  const cw = Math.max(1, Math.round(cropPixels.width * scale));
  const ch = Math.max(1, Math.round(cropPixels.height * scale));
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = cw;
  canvas.height = ch;
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cw,
    ch
  );
  return canvas.toDataURL(type, 0.9);
}

// Wandelt ein Bild (relativer Pfad ODER data-URL) in eine herunterskalierte
// JPEG-data-URL. WICHTIG fürs PDF: es wird serverseitig von Api2Pdf-Chrome
// gerendert, das RELATIVE Pfade (/img.jpg) nicht laden kann -> nur eingebettete
// data-URLs erscheinen in Preview UND PDF identisch. Das Downscaling hält das
// PDF klein und schnell zu öffnen.
const _embeddedImgCache = new Map();
async function toEmbeddedDataUrl(src, maxDim = 1100, quality = 0.85) {
  if (!src) return "";
  if (_embeddedImgCache.has(src)) return _embeddedImgCache.get(src);
  try {
    const img = await createImage(src);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return src.startsWith("data:") ? src : "";
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    canvas.getContext("2d").drawImage(img, 0, 0, cw, ch);
    const out = canvas.toDataURL("image/jpeg", quality);
    _embeddedImgCache.set(src, out);
    return out;
  } catch {
    return src.startsWith("data:") ? src : "";
  }
}
async function waitForImages(root) {
  const imgs = Array.from(root.querySelectorAll("img"));
  const imgPromises = imgs.map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((res) => {
      const done = () => res();
      img.onload = done;
      img.onerror = done;
      if (img.decode) {
        img.decode().then(done).catch(done);
      }
    });
  });

  const bgUrls = new Set();
  const elements = Array.from(root.querySelectorAll("*"));
  const urlRe = /url\((['"]?)(.*?)\1\)/g;
  for (const el of elements) {
    const bg = getComputedStyle(el).backgroundImage;
    if (!bg || bg === "none") continue;
    urlRe.lastIndex = 0;
    let match;
    while ((match = urlRe.exec(bg)) !== null) {
      const url = match[2];
      if (url) bgUrls.add(url);
    }
  }

  const bgPromises = Array.from(bgUrls).map(
    (src) =>
      new Promise((res) => {
        const img = new Image();
        const done = () => res();
        img.onload = done;
        img.onerror = done;
        img.src = src;
        if (img.decode) {
          img.decode().then(done).catch(done);
        }
      }),
  );

  await Promise.all([...imgPromises, ...bgPromises]);
}
function getCoverCropPixels(imgW, imgH, targetAspect) {
  const imgAspect = imgW / imgH;
  if (imgAspect > targetAspect) {
    const cropH = imgH;
    const cropW = Math.round(imgH * targetAspect);
    const x = Math.round((imgW - cropW) / 2);
    return { x, y: 0, width: cropW, height: cropH };
  } else {
    const cropW = imgW;
    const cropH = Math.round(imgW / targetAspect);
    const y = Math.round((imgH - cropH) / 2);
    return { x: 0, y, width: cropW, height: cropH };
  }
}
function normalizeUrl(u) {
  if (!u) return "";
  const hasProto = /^https?:\/\//i.test(u);
  return hasProto ? u : `https://${u}`;
}
function formatTimeHHMM(d) {
  try {
    return new Intl.DateTimeFormat("de-AT", { hour: "2-digit", minute: "2-digit" }).format(d);
  } catch {
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }
}


function StepNotes({ stepKey, locked = false }) {
  const [expanded, setExpanded] = React.useState(false);
  const [md, setMd] = React.useState("");
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setReady(false);
    setMd("");

    fetch(`/notes/${encodeURIComponent(stepKey)}.md`, { cache: "no-store" })
      .then(async (res) => {
        if (res.status === 404) return "";
        if (!res.ok) return "";
        return await res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setMd(text || "");
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setMd("");
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [stepKey]);

  // noch am laden: nix rendern (kein Flackern)
  if (!ready) return null;

  // keine Datei / leerer Inhalt: nix rendern
  if (!md.trim()) return null;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, v) => !locked && setExpanded(v)}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        overflow: "hidden",
        border: "none",
        boxShadow: "none",
        bgcolor: "transparent",
        backgroundImage: "none",
        opacity: locked ? 0.6 : 1,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        sx={{
          minHeight: 40,
          px: 2.5,
          py: 0,

          bgcolor: expanded ? "rgba(125,156,20,0.14)" : "rgba(125,156,20,0.08)",

          borderBottom: "none",
          boxShadow: "none",

          "&.Mui-expanded": { minHeight: 40 },

          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontSize: "0.875rem" }}
        >
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}>
            {locked && <LockOutlinedIcon sx={{ fontSize: 14 }} />}
            {expanded ? "Zusammenfassung schließen" : "Zusammenfassung anzeigen"}
          </Box>
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        onClick={(e) => {
          if (locked) return;
          e.preventDefault();
          e.stopPropagation();
          setExpanded(false);
        }}
        sx={{
          // ✅ Body: NUR helles Grau (immer gleich)
          bgcolor: "rgba(0,0,0,0.035)",

          // ✅ Trenner weg
          borderTop: "none",
          boxShadow: "none",

          px: 1.5,
          pt: 0.75,
          pb: 1.25,
          cursor: "pointer",
          userSelect: "text",
        }}
      >
        <Box
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.55,

            "& h1, & h2, & h3, & h4, & h5, & h6": {
              mt: 0.9,
              mb: 0.25,
              lineHeight: 1.25,
            },
            "& h1 + p, & h2 + p, & h3 + p, & h4 + p, & h5 + p, & h6 + p": {
              mt: 0.25,
            },
            "& h1 + ul, & h2 + ul, & h3 + ul, & h4 + ul, & h5 + ul, & h6 + ul": {
              mt: 0.25,
            },
            "& h1 + ol, & h2 + ol, & h3 + ol, & h4 + ol, & h5 + ol, & h6 + ol": {
              mt: 0.25,
            },

            "& p": { my: 0.5 },
            "& ul, & ol": { pl: 2.5, my: 0.5 },
            "& li": { my: 0.15 },
            "& a": { wordBreak: "break-word" },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
        </Box>
      </AccordionDetails>
    </Accordion>
  );


}




// ======= DEFAULT TEXTS (für Reset) =======
const DEFAULT_HEROTEXT =
  "{Name} verbindet druckvolle {Genre/Mix: z. B. Melodic & Tech House} mit organischen Samples und detailverliebten Arrangements. Ideal für {Event-Typ} und andere Events.";

const DEFAULT_BIO =
  "{Name} ist ein/e {Genre}-Musiker/in aus {Ort}, der/die seit {Jahr} Musik veröffentlicht. Erste Erfolge waren {Highlight 1} und {Highlight 2}. Sein/ihr Sound bewegt sich zwischen {Genre/Einflüsse} und {besondere Merkmale}. {Name} begann mit {Instrument} im Alter von {X Jahren}. Inspiriert von {Einflüsse} entwickelte er/sie einen Stil, der {Beschreibung des Sounds}. Zurzeit arbeitet {Name} an {Projekt/Release/Tour}. Zusätzlich ist er/sie in {Nebenprojekte} involviert. In den kommenden Monaten stehen {Events/Shows} an.";

const DEFAULT_RIDER =
  "Bühne mind. {Bühnenbreite} x {Bühnentiefe} m, Stromanschluss {Strom: z. B. 230 V Schuko} bühnennah, 1x Gesangsmikrofon (z. B. {Mikrofon}), 1x Mikrofonständer, 1x DI-Box oder Klinke-Kabel (3,5 mm auf 2x XLR/Klinke) für Playback, 1x Monitor (Boden oder In-Ear), PA-Anlage passend zur Raumgröße, Mischpult min. 2 Kanäle (Gesang + Playback), Grundbeleuchtung mit weichem Frontlicht.\n\nVorbereitung:\nSoundcheck ca. {Soundcheck-Minuten} Min., Aufbauzeit ca. {Aufbau-Minuten} Min., Zuspielung über eigenes Abspielgerät (Laptop/Smartphone), Backstage: {Backstage-Anforderungen}. Outdoor: überdachte Bühne erforderlich, keine direkte Sonne oder Regen auf Technik/Künstlerin.";

const DEFAULT_REPERTOIRE =
  "Shape of You - Ed Sheeran, Rolling in the Deep - Adele, Billie Jean - Michael Jackson, Uptown Funk - Mark Ronson ft. Bruno Mars, Don't Stop Me Now - Queen, Levitating - Dua Lipa, Valeria - Mark Forster, Flowers - Miley Cyrus, Wake Me Up - Avicii, Chandelier - Sia, Take On Me - a-ha";

const DEFAULT_NAME = "Künstlername";
const DEFAULT_TAGLINE = "Live-Musik, die jedes Event in ein Highlight verwandelt.";
const DEFAULT_ANGEBOT =
  "Ich biete ein 60- bis 90-minutiges Live-Set (Solo/Trio) für Firmenfeiern, Galas und Club-Events. Im Set sind eigene Songs sowie bekannte Covers im Stil {Genre} enthalten; Wunsch-Songs können vorab abgestimmt werden. Technik: Zuspielung über Laptop, 2 Funkmikrofone, Monitoring vor Ort. Benötigte Fläche: ca. 4 x 3 m. Auf- und Abbau in ca. 30 Minuten.";
const DEFAULT_CONTACT_EMAIL_HINT = "max.musterfrau@beispiel.de";
const DEFAULT_CONTACT_PHONE_HINT = "+0 000 0000000";

const DEFAULT_CONTACT_EMAIL = "max.musterfrau@beispiel.de";
const DEFAULT_CONTACT_PHONE = "+0 000 0000000";
const DEFAULT_CONTACT_SUBTITLE = "Ich freue mich auf eine erfolgreiche Zusammenarbeit.";
const DEFAULT_SOCIAL_TAGS = "Instagram @{Instagram}\nTikTok @{TikTok}";
const DEFAULT_SOCIAL_PROOF = "Bekannt aus: Medienname · Bühnen: Festivalname";
const DEFAULT_CTA_URL = "https://dein-link.com";
const DEFAULT_PRESS_MATERIAL_URL = "https://dein-presskit-link.com";
const DEFAULT_PRESS_MATERIAL_TEXT =
  "Im Presskit findest du aktuelle Pressefotos (hoch/quer), Logos in Farbe und Schwarz-Weiß, eine Kurzbio, Technik-Infos sowie Links zu Musik/Video. Bitte nutze die Bilder mit Namensnennung und schreib mir kurz bei Fragen oder Sonderformaten.";

const ELEMENT_ROUNDNESS_OPTIONS = {
  off: "0px",
  low: "12px",
  high: "999px",
};

const DIVIDER_VARIANTS = new Set(["none", "angle-right", "angle-left", "wave", "zigzag", "scallop", "gradient"]);

const normalizeDividerStyle = (value) => (DIVIDER_VARIANTS.has(value) ? value : "none");

const normalizeElementRoundness = (value, legacyValue) => {
  if (value === "off" || value === "low" || value === "high") return value;
  if (legacyValue === "square") return "off";
  if (legacyValue === "round") return "high";
  if (legacyValue === "soft") return "low";
  return "low";
};

// automatisch aus den Vorlagen generiert
const HERO_MENTION_DATA = buildMentionDataFromTemplate(DEFAULT_HEROTEXT);
const BIO_MENTION_DATA = buildMentionDataFromTemplate(DEFAULT_BIO);
const RIDER_MENTION_DATA = buildMentionDataFromTemplate(DEFAULT_RIDER);
const REPERTOIRE_MENTION_DATA = buildMentionDataFromTemplate(DEFAULT_REPERTOIRE);
const SOCIAL_TAGS_MENTION_DATA = buildMentionDataFromTemplate(DEFAULT_SOCIAL_TAGS);


// ======= THEME =======
const theme = createTheme({
  // Primär = Lime mit dunkler Ink-Schrift (Landingpage-Look, kein Violett mehr)
  palette: { primary: { main: PURPLE, contrastText: "#14150c" }, success: { main: SUCCESS } },
  shape: { borderRadius: 12 },
  typography: { button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 } },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          ":hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.12)" },
          "&.Mui-disabled": {
            boxShadow: "none",
            backgroundImage: "none",
          },
        },
        containedPrimary: {
          backgroundImage: `linear-gradient(90deg, #c3f04c 0%, #a3d81e 100%)`,
        },
        outlined: {
          borderWidth: 2,
          borderColor: "rgba(0,0,0,0.12)",
          ":hover": { borderWidth: 2, borderColor: PURPLE, backgroundColor: "rgba(125,156,20,0.05)" },
        },
      },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 8px 24px rgba(0,0,0,0.04)" } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": { borderRadius: 10 },
          "& .MuiFormHelperText-root": { fontSize: "0.72rem", opacity: 0.85 },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } } },
  },
});

// ===== Small helpers / UI =====
function NumberStepIcon({ active, complete, icon, size = 20 }) {
  const bg = active ? PURPLE : complete ? SUCCESS : "#9e9e9e";
  const ringColor = active ? "rgba(125,156,20,.28)" : complete ? "rgba(46,125,50,.25)" : "rgba(0,0,0,.12)";
  const fontSize = Math.max(11, Math.round(size * 0.5));
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: bg,
        color: "#fff",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        fontSize,
        boxShadow: `0 0 0 3px ${ringColor}`,
      }}
    >
      {icon}
    </Box>
  );
}

const InputWithCounter = memo(function InputWithCounter({ value, maxLength, helper, error, ...props }) {
  return (
    <TextField fullWidth value={value} inputProps={maxLength ? { maxLength } : undefined} helperText={`${value?.length ?? 0}${maxLength ? `/${maxLength}` : ""}${helper ? ` — ${helper}` : ""}`} error={!!error} {...props} />
  );
});

const TextareaWithCounter = memo(function TextareaWithCounter({ value, maxLength, helper, rows = 6, error, ...props }) {
  return (
    <TextField fullWidth multiline rows={rows} value={value} inputProps={maxLength ? { maxLength } : undefined} helperText={`${value?.length ?? 0}${maxLength ? `/${maxLength}` : ""}${helper ? ` — ${helper}` : ""}`} error={!!error} {...props} />
  );
});

const heroMentionsFont = {
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
  fontSize: 14,
  lineHeight: 1.5,
};

const heroMentionsStyleBase = {
  control: {
    ...heroMentionsFont,
    position: "relative",
    width: "100%",
    minHeight: 120,
  },
  highlighter: {
    ...heroMentionsFont,
    padding: 8,
    minHeight: 120,
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    overflow: "hidden",
    borderRadius: 10,
    border: "1px solid transparent",
  },
  input: {
    ...heroMentionsFont,
    padding: 8,
    minHeight: 120,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.23)",
    outline: 0,
    margin: 0,                // ⬅️ ganz wichtig, sonst Verschiebung!
    backgroundColor: "transparent",
  },
};




const mentionTagStyle = {
  backgroundColor: "rgba(125,156,20,0.18)",
  borderRadius: 4,
  display: "inline-block",
  padding: "0 2px",
  margin: "0 -2px",
};


const MentionsTextarea = memo(function MentionsTextarea({
  value,
  onChange,
  maxLength,
  error,
  helper,
  data = [],
  placeholder,
  locked = false,
}) {
  const length = value?.length ?? 0;

  const mentionStyle = mentionTagStyle;

  const style = {
    ...heroMentionsStyleBase,
    highlighter: {
      ...heroMentionsStyleBase.highlighter,
    },
    input: {
      ...heroMentionsStyleBase.input,
      borderColor: error ? "#d32f2f" : "rgba(0,0,0,0.23)",
      boxShadow: error
        ? "0 0 0 1px rgba(211,47,47,0.3)"
        : "0 0 0 1px transparent",
    },
  };

  const handleChange = (event, newValue) => {
    if (maxLength && newValue && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }
    onChange(newValue);
  };

  return (
    <Box>
      <MentionsInput
        value={value}
        onChange={handleChange}
        style={style}
        allowSuggestionsAboveCursor
        placeholder={placeholder}
      >
        <Mention
          trigger="{"
          markup="{__id__}"
          data={data}
          style={mentionStyle}
        />
      </MentionsInput>

      <Typography
        variant="caption"
        color={error ? "error" : "text.secondary"}
        sx={{ mt: 0.5 }}
      >
        {`${length}${maxLength ? `/${maxLength}` : ""}${helper ? ` — ${helper}` : ""
          }`}
      </Typography>
    </Box>
  );
});





function Field({ title, description, required, action, locked = false, children }) {
  const lockedInputSx = undefined;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          mb: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, letterSpacing: "-0.02em", mr: "auto" }}
        >
          {title} {required ? "*" : ""}
        </Typography>
        {action}
      </Box>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
          {description}
        </Typography>
      )}
      {locked && (
        <Box
          sx={{
            mb: 0.75,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.8,
            px: 1.2,
            py: 0.35,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "text.secondary",
            border: "1px solid rgba(0,0,0,0.18)",
            bgcolor: "rgba(0,0,0,0.04)",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14 }} />
          Premium
        </Box>
      )}
      <Box sx={lockedInputSx}>{children}</Box>
    </Box>
  );
}


function Paywall({ onBuy, name, tagline }) {
  const displayName = name?.trim() || DEFAULT_NAME;
  const displayTagline = tagline?.trim() || DEFAULT_TAGLINE;



  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2.4} alignItems="center" sx={{ textAlign: "center", py: 3.2 }}>
          {/* MOCKUP */}
          <Box
            sx={{
              mt: 3,
              mb: 5,
              position: "relative",
              width: 270,
              aspectRatio: "1 / 1.414",
            }}
          >
            {/* Seite 2 hinten */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 1,
                overflow: "hidden",
                transform: "rotate(4deg) translate(10px,-16px)",
                transformOrigin: "bottom left",
                boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                border: "1px solid rgba(15,23,42,0.06)",
                zIndex: 0,
              }}
            >
              <Box
                component="img"
                src={MOCKUP_IMG_PAGE2}
                alt="Hintere Seite – Foto"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  filter: "brightness(0.9)",
                }}
              />
            </Box>

            {/* Vorderseite */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 1,
                bgcolor: "background.paper",
                boxShadow: "0 8px 22px rgba(15,23,42,0.22)",
                border: "1px solid rgba(15,23,42,0.08)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                zIndex: 1,
              }}
            >
              {/* 1:1 Hero */}
              <Box sx={{ position: "relative", width: "100%", aspectRatio: "1 / 1", overflow: "hidden" }}>
                <Box
                  component="img"
                  src={MOCKUP_IMG_HERO}
                  alt="Media-Kit Header-Foto Vorschau"
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />

                {/* Name unten */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))",
                    py: 0.4,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 22,
                      textAlign: "left",
                      textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                      letterSpacing: 0.2,
                      width: "100%",
                      pl: "10px",
                    }}
                  >
                    {displayName}
                  </Typography>
                </Box>
              </Box>

              {/* Tagline-Banner */}
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "#2563eb",
                  px: 0.5,
                  height: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 8, 
                    lineHeight: 1,
                    color: "#fff",
                    letterSpacing: 0.4,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {displayTagline}
                </Typography>
              </Box>

              {/* Footer-Foto */}
              <Box sx={{ flex: 1, width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  src={MOCKUP_IMG_FOOTER}
                  alt="Unteres Foto"
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </Box>
            </Box>

          </Box>

          {/* Texte */}
          <Stack spacing={1.2} alignItems="center" sx={{ maxWidth: 420 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Vollversion erforderlich
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Ab diesem Kapitel ist der Media-Kit Generator Teil der{" "}
              <strong>Vollversion</strong>.
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Erstelle in wenigen Minuten ein fertiges PDF-Media-Kit, das du direkt an Veranstalter*innen schicken kannst –
              inklusive Fotos, Bio, Tech Rider und klickbaren Links.
            </Typography>


          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              startIcon={<ShoppingCartCheckoutIcon />}
                onClick={() => handlePremiumClick("lock_card")}
            >
              Jetzt Vollversion sichern
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Wenn du bereits gekauft hast, wird die Vollversion automatisch freigeschaltet,
            sobald du die Seite über deinen Kauf-Link mit Token öffnest. Außerdem hast du den Zugangslink gemeinsam mit der Rechnung via Email geschickt bekommen. Bei Fragen melde dich an office@showkompass.at
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}




// ====== RENDER STEP CONTENT ======
const StepVideo = memo(function StepVideo({ src, locked }) {
  if (!src) return null;

  const embedSrc = useMemo(() => {
    const toYouTubeEmbed = (url) => {
      // Video-ID extrahieren
      let videoId = "";
      if (url.hostname === "youtu.be") {
        videoId = url.pathname.slice(1);
      } else if (url.pathname.startsWith("/embed/")) {
        videoId = url.pathname.replace("/embed/", "");
      } else if (url.searchParams.get("v")) {
        videoId = url.searchParams.get("v") || "";
      }

      const base = videoId
        ? `https://www.youtube.com/embed/${videoId}`
        : url.origin + url.pathname;

      const params = new URLSearchParams({
        controls: "1",
        modestbranding: "1",
        rel: "0",
        iv_load_policy: "3",
        playsinline: "1",
        fs: "0",
      });

      return `${base}?${params.toString()}`;
    };

    const toDriveEmbed = (url) => {
      // Unterstützt u.a.:
      // https://drive.google.com/file/d/FILE_ID/view?usp=sharing
      // https://drive.google.com/open?id=FILE_ID
      // https://drive.google.com/uc?id=FILE_ID&export=download
      // https://docs.google.com/file/d/FILE_ID/...
      let fileId = "";

      // /file/d/<id>/...
      const m = url.pathname.match(/\/file\/d\/([^/]+)/);
      if (m?.[1]) fileId = m[1];

      // open?id=<id> oder uc?id=<id>
      if (!fileId) fileId = url.searchParams.get("id") || "";

      // Fallback: manchmal steckt die ID als letzter Pfadteil drin (selten)
      // (lasse ich bewusst konservativ weg, damit wir nichts kaputt-parsen)

      return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : url.toString();
    };

    try {
      const url = new URL(src);

      // YouTube
      if (url.hostname.includes("youtube.com") || url.hostname.includes("youtu.be")) {
        return toYouTubeEmbed(url);
      }

      // Google Drive / Google Docs (Drive Viewer)
      const isDrive =
        url.hostname === "drive.google.com" ||
        url.hostname === "docs.google.com";

      if (isDrive) {
        return toDriveEmbed(url);
      }

      // Sonst unverändert
      return src;
    } catch {
      return src;
    }
  }, [src]);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ position: "relative", pt: "56.25%" }}>
        <Box
          component="iframe"
          src={embedSrc}
          title="Explainer Video"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
            borderRadius: 1,
            filter: locked ? "blur(6px)" : "none",
          }}
        />
        {locked && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(15,23,42,0.35)",
              color: "#fff",
              gap: 1,
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            <LockOutlinedIcon />
            Premium
          </Box>
        )}
      </Box>
    </Card>
  );
});

// ====== MAIN COMPONENT ======
export default function MediaKitWizard_MUI() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevOverflow = body.style.overflowY;
    const prevGutter = html.style.scrollbarGutter;
    body.style.overflowY = "scroll";
    html.style.scrollbarGutter = "stable";
    return () => {
      body.style.overflowY = prevOverflow;
      html.style.scrollbarGutter = prevGutter;
    };
  }, []);

  // Form state
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState(DEFAULT_CONTACT_EMAIL);
  const [contactPhone, setContactPhone] = useState(DEFAULT_CONTACT_PHONE);
  const [contact, setContact] = useState(DEFAULT_SOCIAL_TAGS);
  const [contactSubtitle, setContactSubtitle] = useState(DEFAULT_CONTACT_SUBTITLE);
  const [genre, setGenre] = useState("");
  const [tagline, setTagline] = useState("");
  const [heroText, setHeroText] = useState(DEFAULT_HEROTEXT);
  const [ctaUrl, setCtaUrl] = useState(DEFAULT_CTA_URL);
  const [ctaLabel, setCtaLabel] = useState("Jetzt ansehen");
  const [ctaSocialProof, setCtaSocialProof] = useState("");
  const [pressMaterialUrl, setPressMaterialUrl] = useState("");
  const [pressMaterialText, setPressMaterialText] = useState("");

  const [bioBlock, setBioBlock] = useState(DEFAULT_BIO);
  const [repertoireBlock, setRepertoireBlock] = useState("");
  const [referencesBlock, setReferencesBlock] = useState("");
  const [riderBlock, setRiderBlock] = useState(DEFAULT_RIDER);
  const [angebotBlock, setAngebotBlock] = useState("");

  // Images (hero + portrait) and cropping
  const [photoUrl, setPhotoUrl] = useState("");
  const [rawPhotoUrl, setRawPhotoUrl] = useState("");
  // Gemessenes Breite/Höhe-Verhältnis des Titelfoto-Rahmens im aktiven Template
  // (wird bei jedem Vorschau-Rebuild aktualisiert; Fallback 1:1).
  const [heroFrameAspect, setHeroFrameAspect] = useState(HERO_ASPECT);
  const [portraitPhotoUrl, setPortraitPhotoUrl] = useState("");
  const [rawPortraitPhotoUrl, setRawPortraitPhotoUrl] = useState("");

  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  // Crop-Box so bemessen, dass sie in das VOLLSTÄNDIG angezeigte Bild passt:
  // das ganze Foto bleibt im Popup sichtbar, die Maske zeigt exakt den Ausschnitt,
  // der im Titelbild des aktiven Templates sichtbar ist. Ohne feste cropSize
  // würde react-easy-crop das Bild hochzoomen, sobald Rahmen- und Bildformat
  // stark voneinander abweichen (z. B. breiter Ticket-Rahmen + Hochformat-Foto).
  const cropContainerRef = useRef(null);
  const [heroCropSize, setHeroCropSize] = useState(null);
  useEffect(() => {
    if (!cropOpen) return undefined;
    const src = rawPhotoUrl || photoUrl;
    if (!src) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const img = await createImage(src);
        const el = cropContainerRef.current;
        if (!el || cancelled) return;
        const scale = Math.min(el.clientWidth / img.width, el.clientHeight / img.height);
        const dispW = img.width * scale;
        const dispH = img.height * scale;
        const aspect = heroFrameAspect || HERO_ASPECT;
        let w = dispW;
        let h = w / aspect;
        if (h > dispH) {
          h = dispH;
          w = h * aspect;
        }
        setHeroCropSize({ width: Math.round(w), height: Math.round(h) });
      } catch {
        /* ignore */
      }
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cropOpen, heroFrameAspect, rawPhotoUrl, photoUrl]);

  const [portraitCropOpen, setPortraitCropOpen] = useState(false);
  const [portraitCrop, setPortraitCrop] = useState({ x: 0, y: 0 });
  const [portraitZoom, setPortraitZoom] = useState(1);
  const [portraitCroppedPixels, setPortraitCroppedPixels] = useState(null);

  // Design (layout) + colors
  const [designId, setDesignId] = useState(resolveDesignId(DEFAULT_DESIGN_ID));
  // wenn kein Standardtemplate ==>  const [designId, setDesignId] = useState("");
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATE_ID); // color palette id
  const activePalette = useMemo(() => (templateId ? COLOR_TEMPLATES.find((t) => t.id === templateId) : null), [templateId]);
  const [useCustomColors, setUseCustomColors] = useState(false);
  const [customColors, setCustomColors] = useState(DEFAULT_CUSTOM_COLORS);
  const [fontId, setFontId] = useState(DEFAULT_FONT_ID);
  const [nameFontId, setNameFontId] = useState(DEFAULT_NAME_FONT_ID);
  const [nameFontSize, setNameFontSize] = useState(DEFAULT_NAME_FONT_SIZE);
  const [nameColorKey, setNameColorKey] = useState(DEFAULT_NAME_COLOR_KEY);
  const [nameShadowEnabled, setNameShadowEnabled] = useState(DEFAULT_NAME_SHADOW_ENABLED);
  const [nameShadowColorKey, setNameShadowColorKey] = useState(DEFAULT_NAME_SHADOW_COLOR_KEY);
  const [nameOutlineEnabled, setNameOutlineEnabled] = useState(DEFAULT_NAME_OUTLINE_ENABLED);
  const [nameOutlineColorKey, setNameOutlineColorKey] = useState(DEFAULT_NAME_OUTLINE_COLOR_KEY);
  const [titleFontId, setTitleFontId] = useState(DEFAULT_TITLE_FONT_ID);
  const [titleFontSize, setTitleFontSize] = useState(DEFAULT_TITLE_FONT_SIZE);
  const [bodyFontSize, setBodyFontSize] = useState(DEFAULT_BODY_FONT_SIZE);
  const [lineSpacing, setLineSpacing] = useState(DEFAULT_LINE_SPACING);
  const [bioImageScale, setBioImageScale] = useState(DEFAULT_BIO_IMAGE_SCALE);
  const [elementRoundness, setElementRoundness] = useState("low");
  // Bild-Ausschnitt verschieben: Hero PRO Template (jedes Design rahmt anders),
  // Portrait global. Werte 0..100 (%) als object-/background-position.
  const [heroOffsets, setHeroOffsets] = useState({}); // designId -> {x,y}
  const [portraitOffset, setPortraitOffset] = useState({ x: 50, y: 50 });
  // Overflow-Sperre für den Textblock: bei jedem Vorschau-Rebuild wird gemessen,
  // mit welchen Schriftgrößen/Zeilenabständen der Bio-Text noch komplett auf die
  // Seite passt. Nicht passende Optionen sind im Editor gesperrt.
  const [bioFormatDisabled, setBioFormatDisabled] = useState({ sizeValues: [], spacingIds: [] });
  // Wird die aktuell gewählte Einstellung ungültig (z. B. weil der Text länger
  // wurde), automatisch auf den nächstkleineren zulässigen Wert zurückfallen.
  useEffect(() => {
    if (bioFormatDisabled.sizeValues.includes(bodyFontSize)) {
      const allowed = BODY_SIZE_OPTIONS.map((o) => o.value).filter(
        (v) => !bioFormatDisabled.sizeValues.includes(v)
      );
      if (allowed.length) {
        const smaller = allowed.filter((v) => v < bodyFontSize);
        setBodyFontSize(smaller.length ? Math.max(...smaller) : Math.min(...allowed));
      }
    }
  }, [bioFormatDisabled, bodyFontSize]);
  useEffect(() => {
    if (bioFormatDisabled.spacingIds.includes(lineSpacing)) {
      const orderedIds = LINE_SPACING_OPTIONS.map((o) => o.id);
      const allowed = orderedIds.filter((id) => !bioFormatDisabled.spacingIds.includes(id));
      if (allowed.length) {
        const curIdx = orderedIds.indexOf(lineSpacing);
        const smaller = allowed.filter((id) => orderedIds.indexOf(id) < curIdx);
        setLineSpacing(smaller.length ? smaller[smaller.length - 1] : allowed[0]);
      }
    }
  }, [bioFormatDisabled, lineSpacing]);
  const [sectionModes, setSectionModes] = useState(buildDefaultSectionModes());
  const [sectionVisibility, setSectionVisibility] = useState({
    bio: true,
    angebot: true,
    repertoire: true,
    rider: true,
    pressMaterial: true,
    contact: true,
  });
  const [dividerStyle, setDividerStyle] = useState("none");
  // Version A: pro Trenner (afterKey -> {type, spacing}). Wird in der Vorschau
  // per Hover am jeweiligen Trenner eingestellt.
  const [dividerConfig, setDividerConfig] = useState({});
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [sectionPadding, setSectionPadding] = useState(buildDefaultSectionPadding());
  const [titleColorKey, setTitleColorKey] = useState("auto");
  const [bodyColorKey, setBodyColorKey] = useState("auto");
  const [footerEnabled, setFooterEnabled] = useState(true);
  const [titleCtaLinkEnabled, setTitleCtaLinkEnabled] = useState(true);
  const [titleCtaQrEnabled, setTitleCtaQrEnabled] = useState(true);

  const REPERTOIRE_BUBBLE_WARNING_LENGTH = 45;

  const normalizeRepertoireItem = (item) =>
    item.replace(/\s*-\s*/g, " | ").replace(/\s*\|\s*/g, " | ").trim();

  const repertoireItems = useMemo(() => {
    return repertoireBlock
      .split(",")
      .map((item) => normalizeRepertoireItem(item))
      .filter(Boolean);
  }, [repertoireBlock]);

  const hasLongRepertoireItem = useMemo(
    () => repertoireItems.some((item) => item.length > REPERTOIRE_BUBBLE_WARNING_LENGTH),
    [repertoireItems]
  );

  // Paywall
  const [isPro, setIsPro] = useState(false);
  const PAYWALL_FROM_INDEX = 2;
  const PREMIUM_STEP_KEYS = new Set(["bio", "repertoire", "rider", "contactCta", "design"]);

  // Busy state for generation
  const [busy, setBusy] = useState(false);
  const hiddenHostRef = useRef(null);

  // Kebab menu + upload
  const [menuAnchor, setMenuAnchor] = useState(null);
  const uploadInputRef = useRef(null);

  // Burger/Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Design editor overlay
  const [designEditorOpen, setDesignEditorOpen] = useState(false);
  const [previewPages, setPreviewPages] = useState([]);
  const [sectionGeometry, setSectionGeometry] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [exportIncompleteOpen, setExportIncompleteOpen] = useState(false);
  const previewTimerRef = useRef(null);
  const previewPendingRef = useRef(false);
  const renderQueueRef = useRef(Promise.resolve());
  const [previewPanePct, setPreviewPanePct] = useState(50);

  // Save indicator
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Error visibility per step
  const [showErrors, setShowErrors] = useState({}); // key -> bool
  const setStepErrorVisible = (key, v) => setShowErrors((s) => ({ ...s, [key]: v }));

  const [purchaseSuccessOpen, setPurchaseSuccessOpen] = useState(false);
  const [purchaseToken, setPurchaseToken] = useState("");
  const [expiredTokenOpen, setExpiredTokenOpen] = useState(false);
  const badge = PRICING?.badge ?? null;
  const [stepRatings, setStepRatings] = useState({});
  const premiumStatusText = useMemo(() => {
    let storedToken = "";
    try {
      storedToken = localStorage.getItem(PRO_KEY) || "";
    } catch { }
    const status = decodeProToken(purchaseToken || storedToken);
    if (status.valid) {
      if (Number.isFinite(status.expiryMs)) {
        const dateLabel = new Date(status.expiryMs).toLocaleDateString("de-DE");
        return `Premium bis ${dateLabel}`;
      }
      return "Premium bis —";
    }
    return "Premium bis —";
  }, [purchaseToken]);

  // ===== Pro-Token aus URL lesen & speichern =====
  useEffect(() => {
    try {
      const url = new URL(window.location.href);

      const t = url.searchParams.get("token") || url.searchParams.get("t");
      const success = url.searchParams.get("success");

      let changed = false;

      if (t) {
        const status = decodeProToken(t);
        if (status.valid) {
          localStorage.setItem(PRO_KEY, t);
          setPurchaseToken(t); // ✅ merken fürs Popup
          setIsPro(true);
        } else {
          localStorage.removeItem(PRO_KEY);
          setIsPro(false);
          if (status.expired) setExpiredTokenOpen(true);
        }

        url.searchParams.delete("token");
        url.searchParams.delete("t");
        changed = true;
      }

      if (success === "1" || success === "true") {
        setPurchaseSuccessOpen(true); // ✅ Popup öffnen
        url.searchParams.delete("success");
        changed = true;
      }

      if (changed) {
        window.history.replaceState({}, document.title, url.toString());
      }
    } catch {
      // ignore
    }
  }, []);


  useEffect(() => {
    try {
      const saved = localStorage.getItem(PRO_KEY);
      const status = decodeProToken(saved);
      if (status.valid) {
        setIsPro(true);
      } else {
        setIsPro(false);
        if (status.expired) {
          localStorage.removeItem(PRO_KEY);
          setExpiredTokenOpen(true);
        }
      }
    } catch { }
  }, []);

  // ===== Wizard steps (NEUE REIHENFOLGE) =====
  const steps = useMemo(
    () => [
      {
        key: "welcome",
        title: "Willkommen",
        // Neuer Opener (Opener_NEW, Drive "NOT DELETE") — bleibt bewusst drin.
        video: "https://drive.google.com/file/d/1T9r7fabmRrClGd9iqwMJ92iSL3-kJ1Co/view?usp=sharing",
        desc: "So startest du durch!",
        validate: () => true,
      },
      {
        key: "nameTagline",
        title: "Name & Tagline",
        video: "https://drive.google.com/file/d/1CZJNKFiu-1Eji2Fml2HkONEf0ri13H6Y/view?usp=sharing",
        desc: "Grundlage für dein Media‑Kit.",
        validate: () => name.trim() && tagline.trim() && heroText.trim() && !hasPlaceholders(tagline),
      },
      {
        key: "bio",
        title: "Bio",
        video: "https://drive.google.com/file/d/1fm1izt7JMryUZU_t24s-VcXpS6ZPYy2r/view?usp=sharing",
        desc: "Stelle dich vor.",
        validate: () => bioBlock.trim() && !hasPlaceholders(bioBlock),
      },
      {
        key: "repertoire",
        title: "Repertoire",
        video: "https://drive.google.com/file/d/1ZgP23YyU3612qgIcqVEcf-y-uHqfhhdk/view?usp=sharing",
        desc: "Songs/Setlist.",
        validate: () =>
          angebotBlock.trim() &&
          repertoireBlock.trim() &&
          !hasPlaceholders(repertoireBlock),
      },

      {
        key: "photos",
        title: "Header & Portraitfoto",
        video: "https://drive.google.com/file/d/1qvVrOU5SzPVv6YLMlUegyWsbGJ0vaM6l/view?usp=sharing",
        desc: "Bilder hochladen & zuschneiden.",
        validate: () => Boolean(photoUrl) && Boolean(portraitPhotoUrl),
      },
      {
        key: "rider",
        title: "Tech Rider",
        video: "https://drive.google.com/file/d/1LPHI5qRawXZK8IFnfDqKA4HcWiI_E7R5/view?usp=sharing",
        desc: "Technische Anforderungen.",
        validate: () => riderBlock.trim() && !hasPlaceholders(riderBlock),
      },
      {
        key: "contactCta",
        title: "Kontakt & Call to Action",
        video: "https://drive.google.com/file/d/1w29HJ17CYtn7a_-KOBrPx4KVu9tYAYSO/view?usp=sharing",
        desc: "Wie erreicht man dich",
        validate: () =>
          contactEmail.trim() &&
          contactPhone.trim() &&
          contact.trim() &&
          !hasPlaceholders(contact) &&
          heroText.trim() &&
          !hasPlaceholders(heroText) &&
          ctaUrl.trim() &&
          ctaLabel.trim(),
      },
      {
        key: "design",
        title: "Design",
        video: "https://drive.google.com/file/d/1SfH0FOYsfhsnDwDmqsOCYngM0uH_FWgR/view?usp=sharing",
        desc: "Dein Media‑Kit finalisieren.",
        validate: () => true,
      },
    ],
    [
      name,
      tagline,
      heroText,
      bioBlock,
      angebotBlock,
      repertoireBlock,
      photoUrl,
      portraitPhotoUrl,
      riderBlock,
      contactEmail,
      contactPhone,
      contact,
      ctaUrl,
      ctaLabel,
    ]
  );

  const [activeStep, setActiveStep] = useState(0);
  const prevStepRef = useRef(null);

  const capturePosthog = (event, payload = {}) => {
    try {
      const ph = window.posthog;
      if (!ph || typeof ph.capture !== "function") return;
      ph.capture(event, payload);
    } catch {
      // ignore
    }
  };

  const isStepValid = (index) => steps[index].validate();
  const isStepComplete = (index) => {
    if (index === steps.length - 1) return false;
    const step = steps[index];
    if (step.key === "references") {
      return !!referencesBlock.trim(); // nur grün, wenn etwas drin ist
    }
    return isStepValid(index);
  };


  const isLocked = (index) => !isPro && PREMIUM_STEP_KEYS.has(steps[index]?.key);

  const canClickStep = () => true;
  const handleStepClick = (idx) => {
    if (!canClickStep(idx)) return;
    setActiveStep(idx);
  };

  const goNext = () => {
    if (activeStep >= steps.length - 1) {
      setDesignEditorOpen(true);
      return;
    }
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleStepRating = (value) => {
    if (!value) return;
    setStepRatings((prev) => ({ ...prev, [activeStep]: value }));
    capturePosthog("mk_step_rating", {
      rating: value,
      stepIndex: activeStep + 1,
      stepKey: steps[activeStep]?.key,
    });
    if (value < 4) {
      setFeedbackOpen(true);
    }
  };

  useEffect(() => {
    if (prevStepRef.current === null) {
      prevStepRef.current = activeStep;
      return;
    }
    if (prevStepRef.current === activeStep) return;

    const fromIndex = prevStepRef.current;
    const toIndex = activeStep;
    capturePosthog("mk_step_change", {
      fromStepIndex: fromIndex + 1,
      toStepIndex: toIndex + 1,
      fromStepKey: steps[fromIndex]?.key,
      toStepKey: steps[toIndex]?.key,
      direction: toIndex > fromIndex ? "forward" : "backward",
      pagePath: window.location.pathname,
    });
    prevStepRef.current = activeStep;
  }, [activeStep, steps]);

  const currentVideoSrc = steps[activeStep]?.video;

  const buildAutosavePayload = () => ({
    name,
    genre,
    tagline,
    contactEmail,
    contactPhone,
    contact,
    contactSubtitle,
    heroText,
    ctaUrl,
    ctaLabel,
    ctaSocialProof,
    pressMaterialUrl,
    pressMaterialText,
    templateId,
    designId,
    useCustomColors,
    customColors,
    bioBlock,
    repertoireBlock,
    referencesBlock,
    angebotBlock,
    riderBlock,
    photoUrl,
    portraitPhotoUrl,
    fontId,
    nameFontId,
    nameFontSize,
    nameColorKey,
    nameShadowEnabled,
    nameShadowColorKey,
    nameOutlineEnabled,
    nameOutlineColorKey,
    titleFontId,
    titleFontSize,
    bodyFontSize,
    lineSpacing,
    bioImageScale,
    elementRoundness,
    heroOffsets,
    portraitOffset,
    footerEnabled,
    titleCtaLinkEnabled,
    titleCtaQrEnabled,
    sectionModes,
    sectionVisibility,
    sectionOrder,
    sectionPadding,
    titleColorKey,
    bodyColorKey,
    dividerStyle: "none",
    dividerConfig,
  });

  // ===== Autosave =====
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(buildAutosavePayload()));
        setLastSavedAt(new Date());
        return;
      }
      const v = JSON.parse(raw);
      if (typeof v.name === "string") setName(v.name);
      if (typeof v.genre === "string") setGenre(v.genre);
      if (typeof v.tagline === "string") setTagline(v.tagline);
      if (typeof v.contactEmail === "string") setContactEmail(v.contactEmail);
      if (typeof v.contactPhone === "string") setContactPhone(v.contactPhone);
      if (typeof v.contactSubtitle === "string") setContactSubtitle(v.contactSubtitle);
      if (typeof v.contactDetails === "string" && (typeof v.contactEmail !== "string" || typeof v.contactPhone !== "string")) {
        const parts = v.contactDetails.split("\n").map((l) => l.trim()).filter(Boolean);
        if (typeof v.contactEmail !== "string") setContactEmail(parts[0] || "");
        if (typeof v.contactPhone !== "string") setContactPhone(parts[1] || "");
      }
      if (typeof v.contact === "string") {
        if (typeof v.contactDetails !== "string" && typeof v.contactEmail !== "string" && typeof v.contactPhone !== "string") {
          const split = splitLegacyContact(v.contact);
          if (split.email) setContactEmail(split.email);
          if (split.phone) setContactPhone(split.phone);
          setContact(split.socialTags || v.contact);
        } else {
          setContact(v.contact);
        }
      }
      if (typeof v.heroText === "string") setHeroText(v.heroText);
      if (typeof v.ctaUrl === "string") setCtaUrl(v.ctaUrl);
      if (typeof v.ctaLabel === "string") setCtaLabel(v.ctaLabel);
      if (typeof v.ctaSocialProof === "string") setCtaSocialProof(v.ctaSocialProof);
      if (typeof v.pressMaterialUrl === "string") setPressMaterialUrl(v.pressMaterialUrl);
      if (typeof v.pressMaterialText === "string") setPressMaterialText(v.pressMaterialText);
      if (typeof v.templateId === "string" && v.templateId) setTemplateId(v.templateId);
      if (typeof v.designId === "string" || typeof v.template === "string") {
        setDesignId(resolveDesignId(typeof v.designId === "string" ? v.designId : v.template));
      }
      if (typeof v.useCustomColors === "boolean") setUseCustomColors(v.useCustomColors);
      if (typeof v.customColors === "object" && v.customColors) setCustomColors(v.customColors);
      if (typeof v.bioBlock === "string") setBioBlock(v.bioBlock);
      if (typeof v.repertoireBlock === "string") setRepertoireBlock(v.repertoireBlock);
      if (typeof v.referencesBlock === "string") setReferencesBlock(v.referencesBlock);
      if (typeof v.angebotBlock === "string") setAngebotBlock(v.angebotBlock);
      if (typeof v.riderBlock === "string") setRiderBlock(v.riderBlock);
      if (typeof v.photoUrl === "string") setPhotoUrl(v.photoUrl);
      if (typeof v.portraitPhotoUrl === "string") setPortraitPhotoUrl(v.portraitPhotoUrl);
      if (typeof v.fontId === "string") setFontId(v.fontId);
      if (typeof v.nameFontId === "string") setNameFontId(v.nameFontId);
      if (typeof v.titleFontId === "string") setTitleFontId(v.titleFontId);
      if (typeof v.nameFontSize === "number") setNameFontSize(v.nameFontSize);
      if (typeof v.nameColorKey === "string" && NAME_COLOR_KEYS.has(v.nameColorKey)) {
        setNameColorKey(v.nameColorKey);
      }
      if (typeof v.nameShadowEnabled === "boolean") setNameShadowEnabled(v.nameShadowEnabled);
      if (typeof v.nameShadowColorKey === "string" && NAME_COLOR_KEYS.has(v.nameShadowColorKey)) {
        setNameShadowColorKey(v.nameShadowColorKey);
      }
      if (typeof v.nameOutlineEnabled === "boolean") setNameOutlineEnabled(v.nameOutlineEnabled);
      if (typeof v.nameOutlineColorKey === "string") setNameOutlineColorKey(v.nameOutlineColorKey);
      if (typeof v.titleFontSize === "number") setTitleFontSize(v.titleFontSize);
      if (typeof v.bodyFontSize === "number") setBodyFontSize(v.bodyFontSize);
      if (typeof v.lineSpacing === "string") setLineSpacing(normalizeLineSpacing(v.lineSpacing));
      if (typeof v.bioImageScale === "number") setBioImageScale(clampBioImageScale(v.bioImageScale));
      if (v.heroOffsets && typeof v.heroOffsets === "object") setHeroOffsets(v.heroOffsets);
      if (v.portraitOffset && typeof v.portraitOffset === "object") setPortraitOffset(v.portraitOffset);
      if (typeof v.elementRoundness === "string" || typeof v.bioImageRadius === "string") {
        setElementRoundness(normalizeElementRoundness(v.elementRoundness, v.bioImageRadius));
      }
      if (typeof v.footerEnabled === "boolean") setFooterEnabled(v.footerEnabled);
      if (typeof v.titleCtaLinkEnabled === "boolean") setTitleCtaLinkEnabled(v.titleCtaLinkEnabled);
      if (typeof v.titleCtaQrEnabled === "boolean") setTitleCtaQrEnabled(v.titleCtaQrEnabled);
      if (typeof v.sectionModes === "object" && v.sectionModes) {
        setSectionModes((prev) => ({ ...prev, ...v.sectionModes }));
      }
      if (typeof v.sectionVisibility === "object" && v.sectionVisibility) {
        setSectionVisibility((prev) => ({ ...prev, ...v.sectionVisibility }));
      }
      if (Array.isArray(v.sectionOrder)) setSectionOrder(normalizeSectionOrder(v.sectionOrder));
      if (typeof v.sectionPadding === "object" && v.sectionPadding) {
        setSectionPadding(normalizeSectionPadding(v.sectionPadding));
      }
      if (typeof v.titleColorKey === "string" && SECTION_TEXT_COLOR_KEYS.has(v.titleColorKey)) {
        setTitleColorKey(v.titleColorKey);
      }
      if (typeof v.bodyColorKey === "string" && SECTION_TEXT_COLOR_KEYS.has(v.bodyColorKey)) {
        setBodyColorKey(v.bodyColorKey);
      }
      if (typeof v.dividerConfig === "object" && v.dividerConfig) {
        setDividerConfig(normalizeDividerConfig(v.dividerConfig));
      }
      if (!v.templateId && !v.useCustomColors) {
        setTemplateId(DEFAULT_TEMPLATE_ID);
      }
      setLastSavedAt(new Date()); // Anzeige: Daten wurden geladen
    } catch (e) {
      console.warn("Autosave laden fehlgeschlagen:", e);
    }
  }, []);

  useEffect(() => {
    const payload = buildAutosavePayload();

    setIsSaving(true);
    const id = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        setLastSavedAt(new Date());
      } catch (e) {
        console.warn("Autosave fehlgeschlagen (Speicher voll?):", e);
      } finally {
        setIsSaving(false);
      }
    }, 350);
    return () => clearTimeout(id);
  }, [
    name,
    genre,
    tagline,
    contactEmail,
    contactPhone,
    contact,
    contactSubtitle,
    heroText,
    ctaUrl,
    ctaLabel,
    ctaSocialProof,
    pressMaterialUrl,
    pressMaterialText,
    templateId,
    designId,
    useCustomColors,
    customColors,
    bioBlock,
    repertoireBlock,
    referencesBlock,
    angebotBlock,
    riderBlock,
    photoUrl,
    portraitPhotoUrl,
    fontId,
    nameFontId,
    nameFontSize,
    nameColorKey,
    nameShadowEnabled,
    nameShadowColorKey,
    nameOutlineEnabled,
    nameOutlineColorKey,
    titleFontId,
    titleFontSize,
    bodyFontSize,
    lineSpacing,
    bioImageScale,
    elementRoundness,
    heroOffsets,
    portraitOffset,
    footerEnabled,
    titleCtaLinkEnabled,
    titleCtaQrEnabled,
    sectionModes,
    sectionVisibility,
    sectionOrder,
    sectionPadding,
    titleColorKey,
    bodyColorKey,
    dividerStyle,
    dividerConfig,
  ]);

  // ===== File handling + Crop dialogs =====
  function onHeroFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setRawPhotoUrl(String(dataUrl));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedPixels(null);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  }

  async function onPortraitFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = async () => {
      const dataUrl = String(r.result || "");
      const img = await createImage(dataUrl);
      const fallback = getCoverCropPixels(img.width, img.height, PORTRAIT_ASPECT);
      setRawPortraitPhotoUrl(dataUrl);
      setPortraitCroppedPixels(fallback);
      setPortraitCrop({ x: 0, y: 0 });
      setPortraitZoom(1);
      setPortraitCropOpen(true);
    };
    r.readAsDataURL(f);
  }

  async function confirmHeroCrop() {
    // Quelle: bevorzugt Originalfoto, sonst das bereits zugeschnittene Bild
    // (rawPhotoUrl wird nicht persistiert -> nach Reload nur photoUrl vorhanden).
    const src = rawPhotoUrl || photoUrl;
    if (!src) return;
    let pixels = croppedPixels;
    if (!pixels) {
      const img = await createImage(src);
      pixels = { x: 0, y: 0, width: img.width, height: img.height };
    }
    const cropped = await getCroppedImg(src, pixels, "image/jpeg");
    setPhotoUrl(cropped);
    setCropOpen(false);
  }
  async function confirmPortraitCrop() {
    const src = rawPortraitPhotoUrl || portraitPhotoUrl;
    if (!src) return;
    let pixels = portraitCroppedPixels;
    if (!pixels) {
      const img = await createImage(src);
      pixels = { x: 0, y: 0, width: img.width, height: img.height };
    }
    const cropped = await getCroppedImg(src, pixels, "image/jpeg");
    setPortraitPhotoUrl(cropped);
    setPortraitCropOpen(false);
  }

  async function buildRenderedPages() {
    const chosenDesign =
      DESIGN_TEMPLATES.find((d) => d.id === designId) ||
      DESIGN_TEMPLATES.find((d) => d.id === DEFAULT_DESIGN_ID) ||
      DESIGN_TEMPLATES[0];
    if (!chosenDesign) throw new Error("Template nicht gefunden.");
    // Layout-HTML kommt aus dem Bundle (kein Runtime-fetch mehr). Versteckte IDs
    // ohne eigenes Layout (minimal/gradients) fallen auf den Default zurück.
    const rawTemplate =
      DESIGN_TEMPLATE_HTML[chosenDesign.id] || DESIGN_TEMPLATE_HTML[DEFAULT_DESIGN_ID];
    if (rawTemplate == null) throw new Error(`Design-Layout fehlt: ${chosenDesign.id}`);
    let html = _templateCache.get(chosenDesign.id);
    if (html == null) {
      html = stripNonPrintMediaQueries(rawTemplate);
      _templateCache.set(chosenDesign.id, html);
    }

    const effectiveName = name.trim() || DEFAULT_NAME;
    const effectiveTagline = tagline.trim() || DEFAULT_TAGLINE;
    const effectiveAngebot = angebotBlock.trim() || DEFAULT_ANGEBOT;
    const effectiveRepertoire = repertoireBlock.trim() || DEFAULT_REPERTOIRE;
    const normalizedRepertoire = effectiveRepertoire.replace(/\s*[-–—]\s*/g, " | ");
    const effectiveContactEmail = contactEmail.trim() || DEFAULT_CONTACT_EMAIL_HINT;
    const effectiveContactPhone = contactPhone.trim() || DEFAULT_CONTACT_PHONE_HINT;
    const effectiveContactSubtitle = contactSubtitle.trim() || DEFAULT_CONTACT_SUBTITLE;
    const effectiveSocialProof = ctaSocialProof.trim() || DEFAULT_SOCIAL_PROOF;
    const effectivePressMaterialText =
      pressMaterialText.trim() || DEFAULT_PRESS_MATERIAL_TEXT;
    const effectivePressMaterialUrl =
      pressMaterialUrl.trim() || DEFAULT_PRESS_MATERIAL_URL;

    const normalizedCtaUrl = normalizeUrl(ctaUrl.trim());
    const normalizedPressUrl = normalizeUrl(effectivePressMaterialUrl);

    const ctaLinkStyle = titleCtaLinkEnabled ? "" : "display:none;";
    let ctaQrDataUrl = "";
    if (normalizedCtaUrl && titleCtaQrEnabled) {
      try {
        ctaQrDataUrl = await cachedQrDataUrl(normalizedCtaUrl);
      } catch (e) {
        console.warn("QR-Code konnte nicht erzeugt werden:", e);
      }
    }
    let pressQrDataUrl = "";
    if (normalizedPressUrl) {
      try {
        pressQrDataUrl = await cachedQrDataUrl(normalizedPressUrl);
      } catch (e) {
        console.warn("Pressematerial-QR konnte nicht erzeugt werden:", e);
      }
    }

    const activeFontOption =
      FONT_OPTIONS.find((f) => f.id === fontId) || FONT_OPTIONS[0];
    const activeNameFontOption =
      FONT_OPTIONS.find((f) => f.id === nameFontId) || activeFontOption;
    const activeTitleFontOption =
      FONT_OPTIONS.find((f) => f.id === titleFontId) || activeFontOption;

    // Bilder IMMER als eingebettete, herunterskalierte data-URL rendern – für
    // Upload UND Beispielbild-Fallback, und identisch für Preview UND PDF. Damit:
    //  1) erscheinen die Bilder auch im PDF (Api2Pdf-Chrome kann keine relativen
    //     Pfade laden, aber data-URLs schon),
    //  2) ist die Seitenaufteilung in Preview und PDF gleich (gleicher Inhalt =
    //     gleiche Sektions-Höhen = gleiche Umbrüche),
    //  3) bleibt das PDF klein/schnell (Downscaling).
    const heroForRender = await toEmbeddedDataUrl(photoUrl || MOCKUP_IMG_HERO, 1100, 0.85);
    const portraitForRender = await toEmbeddedDataUrl(portraitPhotoUrl || MOCKUP_IMG_PAGE2, 900, 0.85);
    const contactDetails = [effectiveContactEmail, effectiveContactPhone]
      .filter((line) => line && line.trim())
      .join("\n");
    const repertoireHtml = escapeHtml(normalizedRepertoire);

    html = html
      .replaceAll("{{name}}", escapeHtml(effectiveName))
      .replaceAll("{{tagline}}", escapeHtml(effectiveTagline))
      .replaceAll("{{contact}}", escapeHtml(contactDetails))
      .replaceAll("{{contactDetails}}", escapeHtml(contactDetails))
      .replaceAll("{{herotext}}", escapeHtml(heroText))
      .replaceAll("{{ctaUrl}}", escapeHtml(ctaUrl))
      .replaceAll("{{ctaLabel}}", escapeHtml(ctaLabel))
      .replaceAll("{{ctaSocialProof}}", escapeHtml(effectiveSocialProof))
      .replaceAll("{{ctaLinkStyle}}", ctaLinkStyle)
      .replaceAll("{{bioBlock}}", escapeHtml(bioBlock))
      .replaceAll("{{repertoireBlock}}", repertoireHtml)
      .replaceAll("{{angebotBlock}}", escapeHtml(effectiveAngebot))
      .replaceAll("{{referencesBlock}}", escapeHtml(referencesBlock || ""))
      .replaceAll("{{riderBlock}}", escapeHtml(riderBlock))
      .replaceAll("{{page2ImageUrl}}", portraitForRender)
      .replaceAll("{{photoDataUrl}}", heroForRender)
      .replaceAll("{{fontFamily}}", activeFontOption.css)
      .replaceAll("{{ctaQrDataUrl}}", ctaQrDataUrl || "");

    let host = hiddenHostRef.current;
    if (!host) {
      host = document.createElement("div");
      host.setAttribute("aria-hidden", "true");
      host.style.position = "absolute";
      host.style.left = "-10000px";
      host.style.top = "0";
      host.style.width = "794px";
      host.style.pointerEvents = "none";
      document.body.appendChild(host);
      hiddenHostRef.current = host;
    }
    host.innerHTML = html;

    const rawPalette = useCustomColors ? customColors : (activePalette || COLOR_TEMPLATES[0]);
    // Text auf Farbe ist immer automatisch: bei benutzerdefinierten Farben wird die
    // Textfarbe direkt aus dem besten Schwarz/Weiß-Kontrast berechnet. Bei Presets
    // bleibt die kuratierte on-Farbe erhalten, solange sie lesbar ist ("deppensicher").
    const palette = {
      ...rawPalette,
      on1: useCustomColors ? autoTextColor(rawPalette.c1) : ensureReadable(rawPalette.on1, rawPalette.c1),
      on2: useCustomColors ? autoTextColor(rawPalette.c2) : ensureReadable(rawPalette.on2, rawPalette.c2),
      on3: useCustomColors
        ? autoTextColor(rawPalette.c3 ?? rawPalette.c1)
        : ensureReadable(rawPalette.on3 ?? rawPalette.on1, rawPalette.c3 ?? rawPalette.c1),
    };
    const { c1, c2, c3, on1, on2, on3 } = palette;
    host.style.setProperty("--c1", c1);
    host.style.setProperty("--c2", c2);
    host.style.setProperty("--c3", c3 ?? c1);
    host.style.setProperty("--on-c1", on1);
    host.style.setProperty("--on-c2", on2);
    host.style.setProperty("--on-c3", on3 ?? on1);
    host.style.setProperty("--proof-bg", c1);
    host.style.setProperty("--proof-on", on1);
    host.style.setProperty("--font-family-main", activeFontOption.css);
    // WICHTIG: Fließtext (Cover-Tagline/Herotext, Sektions-Textblöcke) erbt
    // --font-family-body. Das wurde bisher NICHT gesetzt -> die Textblock-Schriftart
    // hatte keine Wirkung. Jetzt an die gewählte Body-Schrift koppeln.
    host.style.setProperty("--font-family-body", activeFontOption.css);
    // Elemente OHNE eigene font-family-Regel (z. B. Tagline/Kontakt vieler
    // Titel-Templates) erben die Schrift vom Container. Ohne diese Zeile erben
    // sie im Iframe die im Template hartkodierte body-Schrift -> die gewählte
    // Textblock-Schrift wäre in der Vorschau wirkungslos. Muss identisch auch
    // auf dem Preview-/PDF-Wrapper stehen (buildPagePreviewHtml/StandalonePdf).
    host.style.fontFamily = "var(--font-family-body)";
    host.style.setProperty("--font-family-name", activeNameFontOption.css);
    host.style.setProperty("--font-family-title", TITLE_FONT);
    host.style.setProperty("--font-family-section-title", activeTitleFontOption.css);
    // Künstlername automatisch verkleinern: je länger der Name, desto kleiner die
    // Überschrift -> nie Überlauf/Abschneiden, egal welches Template. Der gewählte
    // nameFontSize dient als Obergrenze. Maßgeblich ist das längste (nicht umbrechbare)
    // Wort; zusätzlich dämpft ein sanfter Gesamtlängen-Faktor sehr lange Namen.
    const nameParts = effectiveName.split(/\s+/).filter(Boolean);
    const longestWord = nameParts.reduce((m, w) => Math.max(m, w.length), 0);
    const totalNameLen = effectiveName.trim().length;
    const NAME_AVAIL_W = 600; // konservative Breite (schmalstes Template)
    const NAME_CHAR_W = 0.62; // Zeichenbreiten-Faktor für fette Displayschrift
    const maxByWord = longestWord > 0 ? NAME_AVAIL_W / (NAME_CHAR_W * longestWord) : nameFontSize;
    const maxByTotal = totalNameLen > 22 ? nameFontSize * Math.sqrt(22 / totalNameLen) : nameFontSize;
    const autoNameSize = Math.round(Math.max(30, Math.min(nameFontSize, maxByWord, maxByTotal)));
    host.style.setProperty("--name-font-size", `${autoNameSize}px`);
    host.style.setProperty("--section-title-size", `${titleFontSize}px`);
    host.style.setProperty("--section-body-size", `${bodyFontSize}px`);
    host.style.setProperty("--section-line-height", `${lineSpacingValue(lineSpacing)}`);
    host.style.setProperty(
      "--bio-image-width",
      `${Math.round(BIO_IMAGE_BASE_WIDTH * clampBioImageScale(bioImageScale))}px`
    );
    host.style.setProperty("--divider-color", on3 ?? on1);
    host.style.setProperty("--section-radius", "20px");
    // Standardisierte Komponenten-Maße (QR, CTA, Kicker, Titelfoto) für alle
    // Titel-Templates aus einer Quelle setzen.
    Object.entries(TEMPLATE_TOKENS).forEach(([prop, val]) => {
      host.style.setProperty(prop, val);
    });
    host.style.setProperty(
      "--element-radius",
      ELEMENT_ROUNDNESS_OPTIONS[elementRoundness] || ELEMENT_ROUNDNESS_OPTIONS.low
    );
    const safeNameColorKey = NAME_COLOR_KEYS.has(nameColorKey) ? nameColorKey : "c1";
    host.style.setProperty("--name-color", palette[safeNameColorKey] || palette.c1);
    // Namensschatten wurde entfernt – immer transparent halten.
    host.style.setProperty("--name-shadow", "0 0 0 transparent");
    const outlineColor = palette[nameOutlineColorKey] || palette.c2;
    host.style.setProperty(
      "--name-outline-shadow",
      nameOutlineEnabled
        ? `2px 0 0 ${outlineColor}, -2px 0 0 ${outlineColor}, 0 2px 0 ${outlineColor}, 0 -2px 0 ${outlineColor}, 2px 2px 0 ${outlineColor}, -2px -2px 0 ${outlineColor}, 2px -2px 0 ${outlineColor}, -2px 2px 0 ${outlineColor}`
        : "0 0 0 transparent"
    );

    const pages = Array.from(host.querySelectorAll(".page"));
    if (!pages.length) throw new Error("Keine .page im Template gefunden");
    pages.slice(1).forEach((p) => p.remove());

    // Einzelelemente fürs Hover-Bearbeiten im Editor markieren.
    const nameEl = host.querySelector(".name");
    if (nameEl) nameEl.dataset.elKey = "name";
    // Cover-Fließtexte (Tagline/Herotext): Klick öffnet rechts den Tab "Text".
    host.querySelectorAll(".tagline, .herotext").forEach((el) => {
      el.dataset.elKey = "coverText";
    });

    // Bild-Ausschnitt verschieben: Hero (Cover-Foto) pro Template positionieren.
    const heroPos = heroOffsets[designId] || { x: 50, y: 50 };
    const heroPosStr = `${heroPos.x}% ${heroPos.y}%`;
    const coverPage = host.querySelector(".page");
    if (coverPage) {
      coverPage.querySelectorAll("img").forEach((img) => {
        if (img.classList.contains("cta-qr") || img.classList.contains("press-qr")) return;
        img.style.objectPosition = heroPosStr;
        img.dataset.elKey = "heroImage";
      });
      coverPage.querySelectorAll('[style*="background-image"]').forEach((el) => {
        el.style.backgroundPosition = heroPosStr;
        el.dataset.elKey = "heroImage";
      });
    }

    const nameStyle = document.createElement("style");
    nameStyle.setAttribute("data-name-style", "true");
    nameStyle.textContent = `
      .name{
        text-shadow: var(--name-shadow, 0 0 0 transparent), var(--name-outline-shadow, 0 0 0 transparent);
        white-space: normal;
        overflow-wrap: break-word;
        hyphens: auto;
      }
    `;
    host.appendChild(nameStyle);

    const sectionStyle = document.createElement("style");
    sectionStyle.setAttribute("data-section-style", "true");
    sectionStyle.textContent = `
      .page-sections{
        position: relative;
        width: 794px;
        height: 1123px;
        background: #fff;
        margin: 0;
        break-after: page;
        page-break-after: always;
      }
      .page-sections:last-child{ break-after:auto; page-break-after:auto; }
      .page-sections-inner{
        /* Version A: Inhaltshöhe-Bänder, oben ausgerichtet, KEIN Strecken des
           Inhalts. Footer-Reserve unten freihalten. */
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 0;
        padding: 0;
        padding-bottom: 44px;
        box-sizing: border-box;
      }
      .section-shell{
        background: var(--section-bg, var(--c3));
        display: flex;
        flex-direction: column;
        flex: 0 0 auto;
      }
      /* Bänder teilen sich die Seite proportional (flex-grow per inline-Style).
         Inhalt wird vertikal zentriert, damit der Zusatzplatz gleichmäßig ober-
         und unterhalb verteilt wird statt als Leerraum unten zu klumpen. */
      .section-shell--fill{
        flex: 1 1 auto;
        justify-content: center;
      }
      img{
        max-width:100%;
        height:auto;
        object-fit:cover;
      }
      .section-block{
        border-radius: var(--section-radius, 20px);
        padding: 30px 28px 26px;
        background: var(--section-box, var(--c3));
        color: var(--section-text, var(--on-c3));
        display: flex;
        flex-direction: column;
        gap: 12px;
        box-shadow: none;
        min-height: 0;
        flex: 0 0 auto;
        margin: 0 60px;
      }
      .section-block--bio{
        gap: 12px;
      }
      .section-title{
        margin: 0;
        font-family: var(--font-family-section-title, var(--font-family-main));
        font-size: var(--section-title-size, 32px);
        font-weight: 900;
        line-height: 1.05;
        color: var(--section-title, var(--section-text, var(--on-c3)));
      }
      .section-body-wrap{
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 16px;
        align-items: start;
      }
      .section-body-wrap--bio{
        margin-top: -4px;
        gap: 22px;
        grid-template-columns: minmax(0, 1fr) var(--bio-image-width, 260px);
      }
      /* Wird das Bild kleiner, soll der Text nicht ins Breite laufen, sondern
         (rechtsbündig) näher ans Bild rücken – der Freiraum entsteht links. */
      .section-body-wrap--bio.has-image .section-body--bio{
        max-width: 460px;
        margin-left: auto;
      }
      /* Ohne echtes Portrait-Foto: kein leerer Bildkasten, Text nutzt volle Breite. */
      .section-body-wrap--bio:not(.has-image){
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
      }
      .section-body-wrap--bio:not(.has-image) .section-image--bio{ display: none; }
      .section-body-wrap.has-image{
        grid-template-columns: minmax(0, 1fr) clamp(240px, 32%, 320px);
      }
      .section-bio-grid{
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
        align-items: start;
      }
      .section-bio-text{
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .section-image{
        width: 100%;
        aspect-ratio: 3 / 4;
        border-radius: 16px;
        object-fit: cover;
        box-shadow: none; /* Drop-Shadow wirkte im PDF wie grauer Rahmen */
      }
      .section-image--bio{
        width: var(--bio-image-width, 260px);
        height: auto;
        aspect-ratio: 3 / 4;
        object-fit: cover;
        border-radius: var(--element-radius, 12px);
        box-shadow: none; /* Drop-Shadow wirkte im PDF wie grauer Rahmen */
      }
      .section-image--float{
        float: right;
        width: 220px;
        max-width: 50%;
        margin: 25px 0 12px 24px;
      }
      .section-image[src=""], .section-image:not([src]){ display:none; }
      .section-body--bio::after{
        content: "";
        display: block;
        clear: both;
      }
      .section-body{
        font-size: var(--section-body-size, 13.5px);
        line-height: var(--section-line-height, 1.6);
        text-align: justify;
        text-justify: inter-word;
        white-space: pre-line;
        overflow: hidden;
        color: var(--section-body-color, var(--section-text, var(--on-c3)));
      }
      .section-press-grid{
        display:grid;
        grid-template-columns: minmax(0, 1fr) 160px;
        gap: 5px;
        align-items: start;
      }
      .section-press-grid--single{
        grid-template-columns: minmax(0, 1fr);
      }
      .press-text{
        white-space: pre-line;
        font-size: var(--section-body-size, 13.5px);
        line-height: var(--section-line-height, 1.5);
        text-align: justify;
        text-justify: inter-word;
        color: var(--section-body-color, var(--section-text, var(--on-c3)));
      }
      .press-link-row{
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 10px;
        flex-wrap: wrap;
      }
      .press-link-label{
        font-size: 0.9em;
        font-weight: 600;
      }
      .press-link{
        display:inline-block;
        font-size: 0.9em;
        font-weight: 600;
        color: inherit;
        text-decoration: underline;
        word-break: break-all;
      }
      .press-qr{
        width: 102px;
        height: 102px;
        object-fit: contain;
        border-radius: 14px;
        background: rgba(255,255,255,0.95);
        padding: 6px;
      }
      .press-qr-wrap{
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
      }
      .press-proof{
        margin-top: 16px;
        display: block;
        width: 100%;
        box-sizing: border-box;
        font-weight: 700;
        text-align: center;
        letter-spacing: 0.2px;
        color: inherit;
      }
      .press-proof-label{
        font-size: 0.9em;
        font-weight: 600;
        letter-spacing: 0.4px;
      }
      .press-proof-value{
        margin-top: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        text-transform: uppercase;
        font-size: 1em;
        font-weight: 800;
        letter-spacing: 0.8px;
      }
      .press-proof-value::before,
      .press-proof-value::after{
        content: "";
        flex: 1;
        height: 2px;
        background: var(--c1);
        opacity: 0.45;
      }
      .section-contact-grid{
        display:grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 24px;
        align-items: start;
      }
      .contact-title{
        font-size: var(--section-title-size, 32px);
        font-weight: 900;
        color: var(--section-title, var(--section-text, var(--on-c3)));
      }
      .contact-left{
        font-size: var(--section-body-size, 13.5px);
        line-height: var(--section-line-height, 1.5);
      }
      .contact-right{
        text-align: right;
      }
      .contact-row{
        display:flex;
        align-items: baseline;
        gap: 8px;
      }
      .contact-icon{
        font-size: 0.95em;
        line-height: 1;
      }
      .contact-strong{
        font-weight: 500;
        white-space: pre-line;
      }
      .contact-spacer{
        height: 14px;
      }
      .contact-socials{
        white-space: pre-line;
      }
      .contact-subtitle{
        margin-top: 8px;
        font-size: 1.08em;
        line-height: 1.4;
      }
      /* Version A: echtes Trenner-Element zwischen zwei Bändern. Höhe = spacing,
         Hintergrund (Übergangsgrafik) skaliert via background-size:100% 100%. */
      .section-divider{
        width: 100%;
        flex: 0 0 auto;
        display: block;
        line-height: 0;
        padding: 0;
        border: 0;
        font-size: 0;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        /* 1px-Overlap in beide Nachbarbänder: die Trenner-Oberkante ist die Farbe
           des oberen Bands, die Unterkante die des unteren. Durch das Überlappen
           gibt es keine subpixel-Lücke mehr -> keine Haarlinie unter Welle/Schräge. */
        margin-top: -1px;
        margin-bottom: -1px;
      }
      .section-shell--banner{
        background: var(--section-bg, var(--c3));
      }
      .section-banner{
        margin: 0;
        height: 48px;
        display:flex;
        align-items:center;
        justify-content:center;
        padding:0 24px;
        font-weight:700;
        font-size:18px;
        letter-spacing:.2px;
        text-align:center;
        background-color: var(--section-box, var(--c3));
        background-image: none;
        color: var(--section-text, var(--on-c3));
        font-family: var(--font-family-main);
        border: 1px solid rgba(2,6,23,0.08);
        box-shadow: 0 10px 24px rgba(2,6,23,0.18);
      }
      .section-spacer{
        height: 96px;
      }
      .repertoire-chips{
        display:flex;
        flex-wrap:wrap;
        gap:8px;
        line-height:1.2;
      }
      .repertoire-chip{
        display:inline-flex;
        align-items:center;
        padding:3px 12px 6px;
        border-radius:999px;
        border:1px solid currentColor;
        background: rgba(255,255,255,0.2);
        font-size:0.82em;
        line-height: 1.05;
        white-space:normal;
        overflow-wrap:anywhere;
        max-width:100%;
        vertical-align: middle;
      }
      .repertoire-title{
        font-weight:700;
      }
      .repertoire-artist,
      .repertoire-sep{
        font-weight:400;
      }
      .repertoire-sep{
        opacity:0.8;
      }
      .page-footer{
        position:absolute;
        left:0;
        right:0;
        bottom:0;
        display:flex;
        justify-content:space-between;
        align-items:center;
        /* 1px höher als die reservierten 44px: der Footer überlappt die Unterkante
           des letzten (gefüllten) Bandes um 1px und schluckt so den feinen
           Sub-Pixel-Strich, der sonst an der Naht Band↔Footer sichtbar wird.
           Beide haben dieselbe Farbe -> die Überlappung ist unsichtbar. */
        height: 45px;
        font-size:11px;
        letter-spacing:0.08em;
        text-transform:uppercase;
        padding: 0 60px;
        border-radius: 0;
      }
    `;
    // Minimales, rein dekoratives Rand-Motiv der Inhaltsseiten (nur kuratierte Designs)
    if (SECTION_DECOR[designId]) {
      sectionStyle.textContent += `\n${SECTION_DECOR[designId]}`;
    }
    host.appendChild(sectionStyle);

    // Auf die echten Web-Fonts warten, BEVOR Sektionen vermessen und gepackt
    // werden – sonst basieren Höhen/Umbrüche und Name-Auto-Fit auf Fallback-
    // Metriken und weichen vom finalen (Server-)PDF ab.
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* ignore */
      }
    }

    const sectionImages = {
      bio: portraitForRender,
    };

    const contactLines = [effectiveName, contactDetails, contact, effectiveContactSubtitle].filter(
      (line) => line && line.trim()
    );
    const contactContent = contactLines.join("\n");
    const pressMaterialContent = [effectivePressMaterialText, normalizedPressUrl, effectiveSocialProof].filter(
      (line) => line && line.trim()
    ).join("\n");

    const sectionContentByKey = {
      bio: bioBlock,
      angebot: effectiveAngebot,
      repertoire: effectiveRepertoire,
      rider: riderBlock,
      pressMaterial: pressMaterialContent,
      contact: contactContent,
    };
    const sectionDefs = normalizeSectionOrder(sectionOrder)
      .map((key) => ({ key, content: sectionContentByKey[key], title: SECTION_LABELS[key] }))
      .filter((s) => (s.content || "").trim() && sectionVisibility[s.key] !== false);

    const loadSectionTemplate = (key) => {
      const tpl = SECTION_TEMPLATES[key];
      if (!tpl) throw new Error(`Section template fehlt: ${key}`);
      return tpl;
    };

    const PAGE_HEIGHT = 1123;
    const FOOTER_RESERVE = 44; // Höhe des Footers, die unten frei bleiben muss
    // Farbiger Abstand ganz oben: sitzt als padding-top der ERSTEN Section-Shell
    // jeder Seite (siehe Render-Loop) und muss daher vom nutzbaren Platz jeder
    // Seite abgezogen werden, sonst rutscht die unterste Sektion aus der Seite.
    const TOP_SPACING = 52;
    // Version A: feste Bandhöhen (Inhaltshöhe) + feste Trenner-Abstände. Pro Trenner
    // konfigurierbar (Typ + Abstand) über dividerConfig.
    const dividerConfigState = normalizeDividerConfig(dividerConfig);
    const sectionNodes = [];
    // Nutzbare Inhaltshöhe pro Seite (ohne Footer-Reserve und ohne oberen Abstand).
    const pageContentHeight = PAGE_HEIGHT - FOOTER_RESERVE - TOP_SPACING;

    const sectionCycle = ["c1", "c2", "c3"];
    // Alle Sektionen werden in EINEN Offscreen-Container gehängt und danach in
    // einem einzigen Layout-Durchgang vermessen (statt pro Sektion einen Reflow
    // zu erzwingen). Spürbar schneller bei jedem Vorschau-Rebuild.
    const measureContainer = document.createElement("div");
    measureContainer.style.position = "absolute";
    measureContainer.style.left = "-10000px";
    measureContainer.style.top = "0";
    measureContainer.style.width = "794px";
    for (let i = 0; i < sectionDefs.length; i += 1) {
      const def = sectionDefs[i];
      let sectionHtml = "";
      if (def.key === "pressMaterial") {
        const pressLinkLabel = effectivePressMaterialUrl || normalizedPressUrl;
        const pressLinkHtml = normalizedPressUrl
          ? `<div class="press-link-row"><span class="press-link-label">Link:</span><a class="press-link" href="${escapeHtml(normalizedPressUrl)}" target="_blank" rel="noopener">${escapeHtml(pressLinkLabel)}</a></div>`
          : "";
        const pressQrHtml = pressQrDataUrl
          ? `<img class="press-qr" src="${pressQrDataUrl}" alt="QR-Code Pressematerial">`
          : "";
        const pressTextHtml = effectivePressMaterialText
          ? `<div class="press-text">${escapeHtml(effectivePressMaterialText)}</div>`
          : "";
        let pressProofHtml = "";
        if (effectiveSocialProof) {
          const trimmedProof = effectiveSocialProof.trim();
          let proofLabel = "Bekannt aus";
          let proofValue = trimmedProof;
          const separatorIndex = trimmedProof.indexOf(":");
          if (separatorIndex > -1) {
            const maybeLabel = trimmedProof.slice(0, separatorIndex).trim();
            const maybeValue = trimmedProof.slice(separatorIndex + 1).trim();
            if (maybeLabel) proofLabel = maybeLabel;
            proofValue = maybeValue || trimmedProof;
          }
          pressProofHtml = `
            <div class="press-proof">
              <div class="press-proof-label">${escapeHtml(proofLabel)}</div>
              <div class="press-proof-value"><span>${escapeHtml(proofValue)}</span></div>
            </div>
          `;
        }
        const pressGridClass = pressQrHtml ? "section-press-grid" : "section-press-grid section-press-grid--single";
        sectionHtml = `
          <section class="section-shell">
            <div class="section-block">
              <h2 class="section-title">${escapeHtml(def.title)}</h2>
              <div class="${pressGridClass}">
                <div>
                  ${pressTextHtml}
                  ${pressLinkHtml}
                </div>
                ${pressQrHtml ? `<div class="press-qr-wrap">${pressQrHtml}</div>` : ""}
              </div>
              ${pressProofHtml}
            </div>
          </section>
        `;
      } else if (def.key === "contact") {
        sectionHtml = `
          <section class="section-shell">
            <div class="section-block">
              <div class="section-contact-grid">
                <div class="contact-left">
                  <div class="contact-strong">${escapeHtml(effectiveName)}</div>
                  <div class="contact-row">
                    <span class="contact-icon">&#9993;</span>
                    <span class="contact-strong">${escapeHtml(effectiveContactEmail)}</span>
                  </div>
                  <div class="contact-row">
                    <span class="contact-icon">&#9742;</span>
                    <span class="contact-strong">${escapeHtml(effectiveContactPhone)}</span>
                  </div>
                  <div class="contact-spacer"></div>
                  <div class="contact-socials">${escapeHtml(contact)}</div>
                </div>
                <div class="contact-right">
                  <div class="contact-title">${escapeHtml(def.title)}</div>
                  <div class="contact-subtitle">${escapeHtml(effectiveContactSubtitle)}</div>
                </div>
              </div>
            </div>
          </section>
        `;
      } else {
        const template = loadSectionTemplate(def.key);
        sectionHtml = template
          .replaceAll("{{sectionTitle}}", escapeHtml(def.title))
          .replaceAll(
            "{{sectionBody}}",
            def.key === "repertoire" ? repertoireHtml : escapeHtml(def.content)
          )
          .replaceAll("{{sectionImageUrl}}", sectionImages[def.key] || "");
      }

      const wrapper = document.createElement("div");
      wrapper.innerHTML = sectionHtml.trim();
      const sectionEl = wrapper.firstElementChild;
      if (!sectionEl) continue;

      const bodyWrap = sectionEl.querySelector(".section-body-wrap");
      if (bodyWrap && sectionImages[def.key]) {
        bodyWrap.classList.add("has-image");
      }

      // Portrait-Bild für das Hover-Overlay markieren (Größe + Rundung direkt im Preview).
      if (def.key === "bio") {
        const bioImg = sectionEl.querySelector(".section-image--bio");
        if (bioImg) bioImg.style.objectPosition = `${portraitOffset.x}% ${portraitOffset.y}%`;
        if (bioImg && sectionImages.bio) bioImg.dataset.elKey = "bioImage";
      }

      // Überschrift + Textblöcke markieren: Klick in der Vorschau öffnet rechts
      // direkt den passenden Einstellungs-Tab (Titel bzw. Text).
      sectionEl.querySelectorAll(".section-title").forEach((el) => {
        el.dataset.elKey = "sectionTitle";
      });
      sectionEl.querySelectorAll(".section-body, .press-text").forEach((el) => {
        el.dataset.elKey = "sectionBody";
      });

      const mode = sectionModes[def.key] || sectionCycle[i % sectionCycle.length];
      // mode kann "c1"/"c2"/"c3" ODER eine freie Farbe (#hex) sein.
      const isCustomBg = typeof mode === "string" && mode.startsWith("#");
      const bgKey = mode === "c1" ? "c1" : mode === "c2" ? "c2" : "c3";
      const onBoxKey = mode === "c1" ? "on1" : mode === "c2" ? "on2" : "on3";

      const sectionBg = isCustomBg ? mode : palette[bgKey];
      // Automatische Textfarbe: bei freier Farbe bester Schwarz/Weiß-Kontrast,
      // bei Palettenfarbe die kuratierte on-Farbe.
      const sectionAutoText = isCustomBg ? autoTextColor(mode) : palette[onBoxKey];
      sectionEl.style.setProperty("--section-bg", sectionBg);
      sectionEl.style.setProperty("--section-box", sectionBg);
      sectionEl.style.setProperty("--section-text", sectionAutoText);
      // Auch manuell gewählte Titel-/Body-Farben gegen den Sektions-Hintergrund
      // auf Lesbarkeit prüfen und nötigenfalls auf schwarz/weiß zurückfallen.
      const titleColor = ensureReadable(
        titleColorKey !== "auto" && palette[titleColorKey] ? palette[titleColorKey] : sectionAutoText,
        sectionBg
      );
      const bodyColor = ensureReadable(
        bodyColorKey !== "auto" && palette[bodyColorKey] ? palette[bodyColorKey] : sectionAutoText,
        sectionBg
      );
      sectionEl.style.setProperty("--section-title", titleColor);
      sectionEl.style.setProperty("--section-body-color", bodyColor);

      // Mess-Klon sammeln – gemessen wird gleich gebündelt. Padding entspricht
      // exakt der finalen CSS (.section-block), daher ist die gemessene Höhe = die
      // echte Bandhöhe im PDF (keine künstlichen Zuschläge mehr).
      const measureClone = sectionEl.cloneNode(true);
      measureContainer.appendChild(measureClone);

      sectionNodes.push({
        key: def.key,
        node: sectionEl,
        measureClone,
        height: 0,
        bgColor: sectionBg,
        textColor: sectionAutoText,
      });
    }

    // Overflow-Proben für den Textblock: die Bio-Sektion zusätzlich mit jeder
    // wählbaren Schriftgröße (bei aktuellem Zeilenabstand) und jedem Zeilen-
    // abstand (bei aktueller Schriftgröße) klonen. Aus den gemessenen Höhen
    // ergibt sich, welche Optionen den Text über die Seite hinausragen ließen –
    // diese werden im Editor gesperrt.
    const bioNodeForProbe = sectionNodes.find((n) => n.key === "bio");
    const bioProbes = [];
    if (bioNodeForProbe) {
      BODY_SIZE_OPTIONS.forEach((opt) => {
        const clone = bioNodeForProbe.node.cloneNode(true);
        clone.style.setProperty("--section-body-size", `${opt.value}px`);
        measureContainer.appendChild(clone);
        bioProbes.push({ kind: "size", id: opt.id, value: opt.value, clone });
      });
      LINE_SPACING_OPTIONS.forEach((opt) => {
        const clone = bioNodeForProbe.node.cloneNode(true);
        clone.style.setProperty("--section-line-height", String(opt.value));
        measureContainer.appendChild(clone);
        bioProbes.push({ kind: "spacing", id: opt.id, value: opt.value, clone });
      });
    }

    // EIN Layout-Durchgang: Container anhängen, Bilder einmal abwarten, alle
    // Höhen in einer Schleife auslesen, Container wieder entfernen.
    host.appendChild(measureContainer);
    await waitForImages(measureContainer);
    sectionNodes.forEach((item) => {
      item.height = Math.round(item.measureClone?.getBoundingClientRect().height || 0);
      item.measureClone = null;
    });
    const nextDisabled = { sizeValues: [], spacingIds: [] };
    bioProbes.forEach((p) => {
      const h = Math.round(p.clone.getBoundingClientRect().height || 0);
      p.clone = null;
      if (h > pageContentHeight) {
        if (p.kind === "size") nextDisabled.sizeValues.push(p.value);
        else nextDisabled.spacingIds.push(p.id);
      }
    });
    measureContainer.remove();
    if (bioNodeForProbe) {
      setBioFormatDisabled((prev) =>
        prev.sizeValues.join() === nextDisabled.sizeValues.join() &&
        prev.spacingIds.join() === nextDisabled.spacingIds.join()
          ? prev
          : nextDisabled
      );
    }

    // Version A – einfacher Akkumulations-Packer:
    // Ein Band bleibt auf der aktuellen Seite, solange (bisher genutzte Höhe +
    // Trenner-Abstand + Bandhöhe) in die nutzbare Inhaltshöhe passt. Sonst beginnt
    // es eine neue Seite. Keine Ratio-/Auto-Padding-Mathematik mehr – allein die
    // realen Höhen + die pro-Trenner gewählten Abstände entscheiden den Umbruch.
    const groups = [];
    let current = [];
    let used = 0;
    sectionNodes.forEach((item) => {
      if (current.length === 0) {
        current.push(item);
        used = item.height;
        return;
      }
      const prev = current[current.length - 1];
      const gap = resolveDividerConfig(dividerConfigState, prev.key).spacing;
      const tentative = used + gap + item.height;
      if (tentative > pageContentHeight) {
        groups.push(current);
        current = [item];
        used = item.height;
      } else {
        used = tentative;
        current.push(item);
      }
    });
    if (current.length) groups.push(current);

    groups.forEach((group) => {
      const page = document.createElement("div");
      page.className = "page page-sections";
      // Freie Resthöhe der Seite (vor dem Aufblähen der Bänder) merken: Grundlage
      // für den Leerraum-Tipp im Editor ("Abschnitte umsortieren / Schrift anpassen").
      const usedHeight = group.reduce(
        (acc, item, idx) =>
          acc +
          item.height +
          (idx > 0 ? resolveDividerConfig(dividerConfigState, group[idx - 1].key).spacing : 0),
        0
      );
      page.dataset.freeHeight = String(Math.max(0, Math.round(pageContentHeight - usedHeight)));
      const inner = document.createElement("div");
      inner.className = "page-sections-inner";

      // Freie Resthöhe der Seite fair auf ALLE Bänder verteilen (nicht nur das
      // letzte aufblähen). Verteilung proportional zur Inhaltshöhe: ein großes
      // Band bekommt mehr Luft als ein kleines -> die Seite wirkt gleichmäßig
      // gefüllt statt "letzte Sektion riesig". Inhalt bleibt jeweils oben.
      // Bei nur einer Sektion füllt sie wie bisher die ganze Seite.
      group.forEach((item, idx) => {
        const node = item.node.cloneNode(true);
        node.dataset.sectionKey = item.key;
        node.classList.add("section-shell--fill");
        // flex-grow proportional zur Bandhöhe (min. 1): der Browser verteilt die
        // freie Resthöhe anteilig auf alle Bänder. flex-basis bleibt = Inhaltshöhe.
        node.style.flexGrow = String(Math.max(1, item.height));
        // Abstand ganz oben gehört farblich zur ERSTEN Sektion der Seite: als
        // Innenabstand der ersten (eingefärbten) Section-Shell statt als
        // transparenter Rand des Inners (der sonst die Seiten-Grundfarbe zeigt).
        if (idx === 0) node.style.paddingTop = `${TOP_SPACING}px`;
        inner.appendChild(node);

        if (idx < group.length - 1) {
          const next = group[idx + 1];
          const cfg = resolveDividerConfig(dividerConfigState, item.key);
          const divider = document.createElement("div");
          divider.className = "section-divider";
          divider.dataset.dividerAfter = item.key;
          divider.dataset.dividerType = cfg.type;
          divider.dataset.dividerSpacing = String(cfg.spacing);
          divider.style.height = `${cfg.spacing}px`;
          const dvBg = dividerBackground(cfg.type, item.bgColor, next.bgColor);
          divider.style.background = dvBg.background;
          divider.style.backgroundSize = dvBg.size;
          divider.style.backgroundRepeat = dvBg.repeat;
          divider.style.backgroundPosition = dvBg.position;
          inner.appendChild(divider);
        }
      });

      page.appendChild(inner);
      host.appendChild(page);
    });

    const allPages = Array.from(host.querySelectorAll(".page"));
    allPages.forEach((pageEl, idx) => {
      if (idx === 0) return;
      const footer = document.createElement("div");
      footer.className = "page-footer";
      const left = document.createElement("div");
      left.textContent = footerEnabled ? (name || "Media Kit") : "";
      const right = document.createElement("div");
      right.textContent = footerEnabled ? `Seite ${idx + 1} / ${allPages.length}` : "";
      const lastSection = groups[idx - 1]?.[groups[idx - 1].length - 1];
      if (lastSection) {
        footer.style.background = lastSection.bgColor;
        footer.style.color = toRgba(lastSection.textColor, 0.8);
      }
      footer.appendChild(left);
      footer.appendChild(right);
      pageEl.appendChild(footer);
    });

    if (ctaQrDataUrl && pages[0]) {
      const firstPage = pages[0];
      const ctaWrap = firstPage.querySelector(".cta-wrap");
      const ctaBtn = ctaWrap?.querySelector(".cta");

      if (ctaWrap && ctaBtn) {
        const qrImg = document.createElement("img");
        qrImg.src = ctaQrDataUrl;
        qrImg.alt = "QR-Code zum Call-to-Action";

        Object.assign(qrImg.style, {
          width: "32px",
          height: "32px",
          aspectRatio: "1 / 1",
          objectFit: "contain",
          background: "none",
          padding: "0",
          marginLeft: "12px",
          display: "inline-block",
          flexShrink: "0",
        });
      }
    }

    const finalPages = Array.from(host.querySelectorAll(".page"));
    for (const p of finalPages) await waitForImages(p);

    // Name Auto-Fit: sehr lange Künstlernamen automatisch verkleinern, damit sie
    // nicht aus dem Cover-Panel laufen. Wir schrumpfen nur (nie vergrößern), bis
    // der Name in max. 2 Zeilen ohne horizontalen Überlauf passt.
    const fitNameEl = host.querySelector(".name");
    if (fitNameEl) {
      fitNameEl.style.fontSize = "";
      const cs = window.getComputedStyle(fitNameEl);
      const baseSize = parseFloat(cs.fontSize) || nameFontSize;
      const lineHeightRatio = (parseFloat(cs.lineHeight) || baseSize) / baseSize;
      const containerW = fitNameEl.parentElement?.clientWidth || 698;
      const MIN_NAME_SIZE = 34;
      const MAX_NAME_LINES = 2;
      let size = baseSize;
      for (let i = 0; i < 40; i++) {
        const lineH = size * lineHeightRatio;
        const lines = Math.round(fitNameEl.scrollHeight / lineH);
        const overflowsX = fitNameEl.scrollWidth > containerW + 1;
        if ((lines <= MAX_NAME_LINES && !overflowsX) || size <= MIN_NAME_SIZE) break;
        size = Math.max(MIN_NAME_SIZE, size - 3);
        fitNameEl.style.fontSize = `${size}px`;
      }

      // Name-Farbe absichern ("deppensicher"): die gewählte --name-color kann auf
      // dem Cover-Hintergrund des jeweiligen Designs unlesbar sein (z. B. dunkle
      // Farbe auf dem dunklen Spotlight-Cover). Effektiven Hintergrund hinter dem
      // Namen bestimmen (erste undurchsichtige Elternfläche) und die Farbe nur bei
      // zu geringem Kontrast auf schwarz/weiß kippen – die bewusste Nutzerwahl
      // bleibt erhalten, solange sie lesbar ist.
      let bgEl = fitNameEl;
      let effectiveBg = "";
      while (bgEl) {
        const bc = window.getComputedStyle(bgEl).backgroundColor;
        const m = bc ? bc.match(/rgba?\(([^)]+)\)/i) : null;
        if (m) {
          const parts = m[1].split(",").map((p) => parseFloat(p.trim()));
          const alpha = parts.length >= 4 ? parts[3] : 1;
          if (alpha > 0.05) { effectiveBg = bc; break; }
        }
        bgEl = bgEl.parentElement;
      }
      if (effectiveBg) {
        const currentColor = window.getComputedStyle(fitNameEl).color;
        // Bewusste Farbwahl beim Künstlernamen RESPEKTIEREN: nur eingreifen, wenn der
        // Name praktisch unsichtbar wäre (Farbe ~ Hintergrund). So schlägt jede
        // Farbänderung in jedem Template sichtbar in die Vorschau durch.
        if (contrastRatio(currentColor, effectiveBg) < 1.22) {
          fitNameEl.style.color = autoTextColor(effectiveBg);
        }
      }
    }

    // Seitenverhältnis des Titelfoto-Rahmens im aktiven Template messen: der
    // Crop-Dialog nutzt exakt dieses Verhältnis, damit der Zuschnitt dem
    // tatsächlich sichtbaren Ausschnitt entspricht (Ticket = breit, Spotlight =
    // rund/1:1, …) statt eines fixen 1:1, das unkontrolliert beschnitten würde.
    const coverHeroEl = finalPages[0]?.querySelector('[data-el-key="heroImage"]');
    if (coverHeroEl) {
      const r = coverHeroEl.getBoundingClientRect();
      if (r.width > 40 && r.height > 40) {
        const ratio = Math.round((r.width / r.height) * 1000) / 1000;
        setHeroFrameAspect((prev) => (prev === ratio ? prev : ratio));
      }
    }

    // Geometrie jeder Sektion (relativ zur Seite) für das Hover-Overlay erfassen.
    const geometry = finalPages.map((pageEl) => {
      const pr = pageEl.getBoundingClientRect();
      const sections = Array.from(pageEl.querySelectorAll("[data-section-key]")).map((el) => {
        const r = el.getBoundingClientRect();
        return {
          key: el.dataset.sectionKey,
          top: r.top - pr.top,
          height: r.height,
        };
      });
      const elements = Array.from(pageEl.querySelectorAll("[data-el-key]")).map((el) => {
        const r = el.getBoundingClientRect();
        return {
          key: el.dataset.elKey,
          left: r.left - pr.left,
          top: r.top - pr.top,
          width: r.width,
          height: r.height,
        };
      });
      // Trenner-Geometrie fürs Hover-Bearbeiten (Typ + Abstand direkt am Trenner).
      const dividers = Array.from(pageEl.querySelectorAll("[data-divider-after]")).map((el) => {
        const r = el.getBoundingClientRect();
        return {
          afterKey: el.dataset.dividerAfter,
          type: el.dataset.dividerType || DEFAULT_DIVIDER_TYPE,
          spacing: Number(el.dataset.dividerSpacing) || DEFAULT_DIVIDER_SPACING,
          top: r.top - pr.top,
          height: r.height,
        };
      });
      return {
        width: pr.width || 794,
        height: pr.height || 1123,
        sections,
        elements,
        dividers,
        freeHeight: Number(pageEl.dataset.freeHeight || 0),
      };
    });

    return { pages: finalPages, normalizedCtaUrl, geometry };
  }

  // ===== Generate PDF =====
  const handleDownloadPdf = () => {
    capturePosthog("mk_pdf_download", {
      stepIndex: activeStep + 1,
      stepKey: steps[activeStep]?.key,
      isPro,
    });
    onGenerate(false);
  };

  const handlePremiumClick = (placement) => {
    capturePosthog("mk_premium_click", { placement, isPro });
    navigate("/mediakit/premium");
  };

  const handleRefreshPreviewClick = () => {
    capturePosthog("mk_preview_reload", { source: "manual", isPro });
    refreshPreview();
  };

  // DEBUG: füllt alle Textfelder mit zufällig langem Lorem-Ipsum, um Layout/Spacing/
  // Übergänge mit unterschiedlich langen Inhalten zu testen.
  const handleFillLorem = () => {
    const POOL = "lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi aliquip ex ea commodo consequat duis aute irure reprehenderit voluptate velit esse cillum eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt culpa officia deserunt mollit anim id est laborum".split(" ");
    const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    const words = (n) => Array.from({ length: n }, () => POOL[rnd(0, POOL.length - 1)]).join(" ");
    const sentence = (min = 6, max = 14) => cap(words(rnd(min, max))) + ".";
    const paragraph = (minS = 2, maxS = 5) =>
      Array.from({ length: rnd(minS, maxS) }, () => sentence()).join(" ");
    const lines = (minN, maxN, minW, maxW) =>
      Array.from({ length: rnd(minN, maxN) }, () => cap(words(rnd(minW, maxW)))).join("\n");
    // In ~60% der Fälle EXTREM lange Inhalte, um Mehrseiten-Umbruch/Spacing/Übergänge
    // bis an die Grenze zu testen.
    const long = () => Math.random() < 0.6;
    // An Wortgrenze auf das maxLength des jeweiligen Feldes kürzen (kein abgehacktes Wort),
    // damit der Wert exakt dem entspricht, was die Textbox erlaubt.
    const clamp = (s, max) => {
      if (!max || s.length <= max) return s;
      let cut = s.slice(0, max);
      const lastBreak = Math.max(cut.lastIndexOf(" "), cut.lastIndexOf("\n"));
      if (lastBreak > max * 0.5) cut = cut.slice(0, lastBreak);
      return cut.trim();
    };

    // Feld-Limits gemäß Eingabemasken (unbegrenzte Felder: kein clamp).
    setName(cap(words(rnd(1, long() ? 7 : 3))));
    setTagline(clamp(cap(words(rnd(3, long() ? 24 : 9))), 70));
    setHeroText(clamp(long() ? paragraph(8, 20) : paragraph(2, 6), 250));
    setBioBlock(clamp(long() ? paragraph(14, 40) : paragraph(3, 9), 2500));
    setAngebotBlock(clamp(long() ? lines(10, 28, 3, 18) : lines(3, 8, 2, 8), 1000));
    setRepertoireBlock(
      clamp(
        Array.from({ length: rnd(4, long() ? 40 : 12) }, () => cap(words(rnd(1, 5)))).join(" - "),
        2000
      )
    );
    setRiderBlock(clamp(long() ? lines(12, 32, 3, 18) : lines(3, 9, 2, 8), 2500));
    setPressMaterialText(clamp(long() ? paragraph(6, 16) : paragraph(1, 4), 400));
    setContactSubtitle(clamp(cap(words(rnd(2, long() ? 16 : 6))), 140));
    setContactEmail(`${words(1)}@example.com`);
    setContactPhone(`+49 ${rnd(100, 999)} ${rnd(1000000, 9999999)}`);
    setCtaSocialProof(
      clamp(
        `Bekannt aus: ${Array.from({ length: rnd(2, long() ? 10 : 4) }, () => cap(words(1))).join(", ")}`,
        200
      )
    );
    setContact(
      clamp(Array.from({ length: rnd(2, long() ? 10 : 4) }, () => `@${words(1)}`).join("\n"), 300)
    );
    // Zufälliger Link (CTA-URL) – wird sonst nie zufällig gesetzt.
    const tld = ["com", "de", "at", "live", "band", "studio"][rnd(0, 5)];
    setCtaUrl(`https://www.${words(1)}${Math.random() < 0.5 ? "-" + words(1) : ""}.${tld}`);
  };

  // Minimum: überall nur EIN Wort – prüft, wie die Designs mit sehr kurzem Text wirken.
  const handleFillMinimum = () => {
    setName("Alex");
    setTagline("Musik");
    setHeroText("Musik.");
    setBioBlock("Musik.");
    setAngebotBlock("Musik");
    setRepertoireBlock("Musik");
    setRiderBlock("Musik");
    setPressMaterialText("Musik.");
    setContactSubtitle("Kontakt");
    setContactEmail("a@b.co");
    setContactPhone("+49 1");
    setCtaSocialProof("Live");
    setCtaLabel("Los");
    setContact("@alex");
  };

  // Maximum: jedes Textfeld bis an sein Zeichenlimit füllen (an Wortgrenze),
  // um Umbruch/Spacing/Übergänge an der Obergrenze zu testen.
  const handleFillMaximum = () => {
    const POOL = "lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi aliquip ex ea commodo".split(" ");
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    // Fülle einen String mit Wörtern bis (fast) genau max Zeichen, an Wortgrenze.
    const fill = (max) => {
      let out = "";
      let i = 0;
      while (true) {
        const w = POOL[i % POOL.length];
        i++;
        const next = out ? out + " " + w : w;
        if (next.length > max) break;
        out = next;
      }
      return cap(out) || cap(POOL[0]).slice(0, max);
    };
    // Mehrzeiliges Feld bis max füllen (Zeilen à ein paar Wörter).
    const fillLines = (max) => {
      let out = "";
      let i = 0;
      while (true) {
        const line = cap([POOL[i % POOL.length], POOL[(i + 1) % POOL.length], POOL[(i + 2) % POOL.length]].join(" "));
        i += 3;
        const next = out ? out + "\n" + line : line;
        if (next.length > max) break;
        out = next;
      }
      return out || fill(max);
    };
    setName(fill(48));
    setTagline(fill(70));
    setHeroText(fill(250));
    setBioBlock(fill(2500));
    setAngebotBlock(fillLines(1000));
    setRepertoireBlock(fill(2000));
    setRiderBlock(fillLines(2500));
    setPressMaterialText(fill(400));
    setContactSubtitle(fill(140));
    setContactEmail(`${fill(20).replace(/\s+/g, ".").toLowerCase()}@example.com`);
    setContactPhone("+49 30 1234567890");
    setCtaSocialProof(fill(200));
    setCtaLabel(fill(28));
    setContact(fillLines(300));
  };

  const enqueueRender = (task) => {
    const next = renderQueueRef.current.then(task, task);
    renderQueueRef.current = next.catch(() => {});
    return next;
  };

  async function onGenerate(previewOnly = false, allowIncomplete = false) {
    if (!isPro) {
      alert("PDF-Download ist nur in der Premium-Version verfügbar.");
      return;
    }
    if (!previewOnly && !allowIncomplete) {
      // final validation: alle Steps müssen valid sein
      const firstInvalidIdx = steps.findIndex((s) => !s.validate());
      if (firstInvalidIdx !== -1) {
        const key = steps[firstInvalidIdx].key;
        setActiveStep(firstInvalidIdx);
        setStepErrorVisible(key, true);
        setExportIncompleteOpen(true);
        return;
      }
    }

    setBusy(true);
    try {
      await enqueueRender(async () => {
        const { pages, normalizedCtaUrl } = await buildRenderedPages();

        if (previewOnly) {
          const preview = window.open("", "_blank", "noopener,noreferrer");
          if (preview) {
            preview.document.title = "Vorschau";
            preview.document.body.style.margin = "0";
            pages.forEach((p) => preview.document.body.appendChild(p.cloneNode(true)));
          }
          return;
        }

        const filename = `${slugify(name)}.pdf`;

        // 1) Bevorzugt: serverseitiges, browser-unabhängiges PDF via Api2Pdf.
        //    Rendert immer in derselben Chrome-Engine → auf jedem Gerät identisch,
        //    echter Vektor-Text. Bei Fehler/Offline fällt es auf den lokalen
        //    html2canvas-Weg zurück (siehe unten), damit der Download nie blockiert.
        const host = hiddenHostRef.current;
        if (host) {
          try {
            const docHtml = buildStandalonePdfHtml(host);
            const res = await fetch("/api/pdf", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ html: docHtml, fileName: filename }),
            });
            const contentType = res.headers.get("content-type") || "";
            if (res.ok && contentType.includes("application/pdf")) {
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 120000);
              return; // Server-PDF erfolgreich → fertig
            }
            let msg = `HTTP ${res.status}`;
            try {
              const j = await res.json();
              if (j && j.error) msg = j.error;
            } catch {
              /* ignore */
            }
            console.warn("Server-PDF nicht verfügbar – Fallback auf lokal:", msg);
          } catch (e) {
            console.warn("Server-PDF fehlgeschlagen – Fallback auf lokal:", e);
          }
        }

        // 2) Fallback: lokales html2canvas-PDF (wie bisher).
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
        for (let i = 0; i < pages.length; i++) {
          const pageEl = pages[i];
          const canvas = await html2canvas(pageEl, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
          });
          const imgData = canvas.toDataURL("image/jpeg", 0.98);
          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();
          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", 0, 0, pageW, pageH, undefined, "FAST");

          // klickbare Fläche auf dem CTA-Button hinterlegen
          if (i === 0) {
            const ctaEl = pageEl.querySelector(".cta");
            const url = normalizedCtaUrl; // <--- gleicher Link wie im QR
            if (ctaEl && url) {
              const pr = pageEl.getBoundingClientRect();
              const br = ctaEl.getBoundingClientRect();
              const x = ((br.left - pr.left) / pr.width) * pageW;
              const y = ((br.top - pr.top) / pr.height) * pageH;
              const w = (br.width / pr.width) * pageW;
              const h = (br.height / pr.height) * pageH;
              pdf.link(x, y, w, h, { url });
            }
          }
        }

        const blob = pdf.output("blob");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      });
    } catch (err) {
      console.error(err);
      alert("Beim Erzeugen des PDFs ist etwas schiefgelaufen.");
    } finally {
      setBusy(false);
    }
  }

  const refreshPreview = async () => {
    if (previewLoading) {
      previewPendingRef.current = true;
      return;
    }
    setPreviewError("");
    setPreviewLoading(true);

    try {
      await enqueueRender(async () => {
        const { pages, geometry } = await buildRenderedPages();
        const host = hiddenHostRef.current;
        // Live-DOM-Vorschau: jede Seite als eigenständiges HTML-Dokument (exakt
        // dieselbe Quelle wie das finale Server-PDF) statt langsamem html2canvas-
        // Screenshot. Ergebnis: vektorscharf, Text markierbar, praktisch
        // latenzfrei – und die Vorschau entspricht 1:1 dem PDF.
        const docs = host ? pages.map((p) => buildPagePreviewHtml(host, p)) : [];
        setPreviewPages(docs);
        setSectionGeometry(geometry || []);
      });
    } catch (err) {
      console.error(err);
      setPreviewError("Vorschau konnte nicht erstellt werden.");
    } finally {
      setPreviewLoading(false);
      if (previewPendingRef.current) {
        previewPendingRef.current = false;
        refreshPreview();
      }
    }
  };

  useEffect(() => {
    if (!designEditorOpen) return;
    refreshPreview();
  }, [designEditorOpen]);

  useEffect(() => {
    if (!designEditorOpen) return;
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      refreshPreview();
    }, 300);
    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [
    designEditorOpen,
    templateId,
    designId,
    useCustomColors,
    customColors,
    fontId,
    nameFontId,
    titleFontId,
    nameFontSize,
    nameColorKey,
    nameShadowEnabled,
    nameShadowColorKey,
    nameOutlineEnabled,
    nameOutlineColorKey,
    titleFontSize,
    bodyFontSize,
    lineSpacing,
    bioImageScale,
    elementRoundness,
    heroOffsets,
    portraitOffset,
    footerEnabled,
    titleCtaLinkEnabled,
    titleCtaQrEnabled,
    sectionModes,
    sectionVisibility,
    sectionOrder,
    sectionPadding,
    titleColorKey,
    bodyColorKey,
    dividerStyle,
    dividerConfig,
    name,
    genre,
    tagline,
    contactEmail,
    contactPhone,
    contact,
    contactSubtitle,
    heroText,
    ctaUrl,
    ctaLabel,
    ctaSocialProof,
    pressMaterialUrl,
    pressMaterialText,
    bioBlock,
    repertoireBlock,
    referencesBlock,
    angebotBlock,
    riderBlock,
    photoUrl,
    portraitPhotoUrl,
  ]);

  useEffect(() => {
    if (previewIndex >= previewPages.length) {
      setPreviewIndex(0);
    }
  }, [previewIndex, previewPages.length]);


  // ====== MENU ACTIONS ======
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const payload = () => ({
    name,
    genre,
    tagline,
    contactEmail,
    contactPhone,
    contact,
    contactSubtitle,
    heroText,
    ctaUrl,
    ctaLabel,
    ctaSocialProof,
    pressMaterialUrl,
    pressMaterialText,
    templateId,
    designId,
    useCustomColors,
    customColors,
    bioBlock,
    repertoireBlock,
    referencesBlock,
    angebotBlock,
    riderBlock,
    photoUrl,
    portraitPhotoUrl,
    fontId,
    nameFontId,
    sectionModes,
    nameFontSize,
    nameColorKey,
    nameShadowEnabled,
    nameShadowColorKey,
    titleFontId,
    titleFontSize,
    bodyFontSize,
    lineSpacing,
    bioImageScale,
    elementRoundness,
    heroOffsets,
    portraitOffset,
    footerEnabled,
    titleCtaLinkEnabled,
    titleCtaQrEnabled,
    sectionVisibility,
    sectionOrder,
    sectionPadding,
    titleColorKey,
    bodyColorKey,
    dividerStyle: "none",
    dividerConfig,
  });

  const isCustomActive = useCustomColors === true;
  const visibleDesignTemplates = useMemo(() => {
    if (!isMobile) return SELECTABLE_DESIGN_TEMPLATES;
    return SELECTABLE_DESIGN_TEMPLATES.filter((design) => !["classic", "bubbles"].includes(design.id));
  }, [isMobile]);
  const visibleColorTemplates = useMemo(() => {
    if (designId !== "spotlight") return COLOR_TEMPLATES;
    return COLOR_TEMPLATES.filter((palette) => !SPOTLIGHT_HIDDEN_COLOR_IDS.has(palette.id));
  }, [designId]);
  useEffect(() => {
    if (useCustomColors) return;
    if (designId !== "spotlight") return;
    if (!SPOTLIGHT_HIDDEN_COLOR_IDS.has(templateId)) return;
    const fallbackId = visibleColorTemplates[0]?.id || "";
    if (fallbackId) setTemplateId(fallbackId);
  }, [designId, templateId, useCustomColors, visibleColorTemplates]);
  const previewPalette = useCustomColors ? customColors : (activePalette || COLOR_TEMPLATES[0]);
  const handleDesignTemplateChange = useCallback(
    (nextDesignCandidate) => {
      const nextDesignId = resolveDesignId(nextDesignCandidate);
      if (!nextDesignId || nextDesignId === designId) return;

      setDesignId(nextDesignId);

      setFontId(DEFAULT_FONT_ID);
      setNameFontId(DEFAULT_NAME_FONT_ID);
      setNameFontSize(DEFAULT_NAME_FONT_SIZE);
      setTitleFontId(DEFAULT_TITLE_FONT_ID);
      setTitleFontSize(DEFAULT_TITLE_FONT_SIZE);
      setBodyFontSize(DEFAULT_BODY_FONT_SIZE);
      setLineSpacing(DEFAULT_LINE_SPACING);
      setBioImageScale(DEFAULT_BIO_IMAGE_SCALE);
      setNameShadowEnabled(DEFAULT_NAME_SHADOW_ENABLED);
      setNameOutlineEnabled(DEFAULT_NAME_OUTLINE_ENABLED);
    },
    [designId]
  );

  const handleDownloadJson = () => {
    capturePosthog("mk_form_download", {
      stepIndex: activeStep + 1,
      stepKey: steps[activeStep]?.key,
      isPro,
    });
    try {
      const data = JSON.stringify(payload(), null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slugify(name) || "mediakit"}.mkit`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Konnte JSON nicht herunterladen.");
    } finally {
      closeMenu();
    }
  };

  const handleResetAll = () => {
    closeMenu();
    if (window.confirm("Formular wirklich zurücksetzen? Alle Eingaben werden gelöscht.")) {
      localStorage.removeItem(STORAGE_KEY);
      window.location.reload();
    }
  };

  const triggerUpload = () => {
    closeMenu();
    uploadInputRef.current?.click();
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (typeof data.name === "string") setName(data.name);
      if (typeof data.genre === "string") setGenre(data.genre);
      if (typeof data.tagline === "string") setTagline(data.tagline);
      if (typeof data.contactEmail === "string") setContactEmail(data.contactEmail);
      if (typeof data.contactPhone === "string") setContactPhone(data.contactPhone);
      if (typeof data.contactSubtitle === "string") setContactSubtitle(data.contactSubtitle);
      if (typeof data.contactDetails === "string" && (typeof data.contactEmail !== "string" || typeof data.contactPhone !== "string")) {
        const parts = data.contactDetails.split("\n").map((l) => l.trim()).filter(Boolean);
        if (typeof data.contactEmail !== "string") setContactEmail(parts[0] || "");
        if (typeof data.contactPhone !== "string") setContactPhone(parts[1] || "");
      }
      if (typeof data.contact === "string") {
        if (typeof data.contactDetails !== "string" && typeof data.contactEmail !== "string" && typeof data.contactPhone !== "string") {
          const split = splitLegacyContact(data.contact);
          if (split.email) setContactEmail(split.email);
          if (split.phone) setContactPhone(split.phone);
          setContact(split.socialTags || data.contact);
        } else {
          setContact(data.contact);
        }
      }
      if (typeof data.heroText === "string") setHeroText(data.heroText);
      if (typeof data.ctaUrl === "string") setCtaUrl(data.ctaUrl);
      if (typeof data.ctaLabel === "string") setCtaLabel(data.ctaLabel);
      if (typeof data.ctaSocialProof === "string") setCtaSocialProof(data.ctaSocialProof);
      if (typeof data.pressMaterialUrl === "string") setPressMaterialUrl(data.pressMaterialUrl);
      if (typeof data.pressMaterialText === "string") setPressMaterialText(data.pressMaterialText);
      if (typeof data.templateId === "string") setTemplateId(data.templateId);
      if (typeof data.designId === "string" || typeof data.template === "string") {
        setDesignId(resolveDesignId(typeof data.designId === "string" ? data.designId : data.template));
      }
      if (typeof data.useCustomColors === "boolean") setUseCustomColors(data.useCustomColors);
      if (typeof data.customColors === "object" && data.customColors) setCustomColors(data.customColors);
      if (typeof data.bioBlock === "string") setBioBlock(data.bioBlock);
      if (typeof data.repertoireBlock === "string") setRepertoireBlock(data.repertoireBlock);
      if (typeof data.referencesBlock === "string") setReferencesBlock(data.referencesBlock);
      if (typeof data.angebotBlock === "string") setAngebotBlock(data.angebotBlock);
      if (typeof data.riderBlock === "string") setRiderBlock(data.riderBlock);
      if (typeof data.photoUrl === "string") setPhotoUrl(data.photoUrl);
      if (typeof data.portraitPhotoUrl === "string") setPortraitPhotoUrl(data.portraitPhotoUrl);
      if (typeof data.fontId === "string") setFontId(data.fontId);
      if (typeof data.titleFontId === "string") setTitleFontId(data.titleFontId);
      if (typeof data.nameFontSize === "number") setNameFontSize(data.nameFontSize);
      if (typeof data.titleFontSize === "number") setTitleFontSize(data.titleFontSize);
      if (typeof data.bodyFontSize === "number") setBodyFontSize(data.bodyFontSize);
      if (typeof data.lineSpacing === "string") setLineSpacing(normalizeLineSpacing(data.lineSpacing));
      if (typeof data.bioImageScale === "number") setBioImageScale(clampBioImageScale(data.bioImageScale));
      if (typeof data.elementRoundness === "string" || typeof data.bioImageRadius === "string") {
        setElementRoundness(normalizeElementRoundness(data.elementRoundness, data.bioImageRadius));
      }
      if (typeof data.footerEnabled === "boolean") setFooterEnabled(data.footerEnabled);
      if (typeof data.titleCtaLinkEnabled === "boolean") setTitleCtaLinkEnabled(data.titleCtaLinkEnabled);
      if (typeof data.titleCtaQrEnabled === "boolean") setTitleCtaQrEnabled(data.titleCtaQrEnabled);
      if (typeof data.sectionModes === "object" && data.sectionModes) {
        setSectionModes((prev) => ({ ...prev, ...data.sectionModes }));
      }
      if (typeof data.sectionVisibility === "object" && data.sectionVisibility) {
        setSectionVisibility((prev) => ({ ...prev, ...data.sectionVisibility }));
      }
      if (Array.isArray(data.sectionOrder)) setSectionOrder(normalizeSectionOrder(data.sectionOrder));
      if (typeof data.sectionPadding === "object" && data.sectionPadding) {
        setSectionPadding(normalizeSectionPadding(data.sectionPadding));
      }
      if (typeof data.dividerConfig === "object" && data.dividerConfig) {
        setDividerConfig(normalizeDividerConfig(data.dividerConfig));
      }
      if (typeof data.titleColorKey === "string" && SECTION_TEXT_COLOR_KEYS.has(data.titleColorKey)) {
        setTitleColorKey(data.titleColorKey);
      }
      if (typeof data.bodyColorKey === "string" && SECTION_TEXT_COLOR_KEYS.has(data.bodyColorKey)) {
        setBodyColorKey(data.bodyColorKey);
      }
      // Divider aktuell fix auf "none"
    } catch (err) {
      alert("Ungültige oder beschädigte JSON-Datei.");
    } finally {
      e.target.value = ""; // reset
    }
  };


  const renderStep = (index) => {
    const s = steps[index];
    const locked = isLocked(index);
    const lockedContentSx = locked ? { pointerEvents: "none" } : undefined;
    switch (s.key) {
      case "welcome": {
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>Willkommen zum Media‑Kit Generator</Typography>
                  <Typography variant="body1" color="text.secondary">In wenigen Schritten zu deinem professionellen PDF‑Media‑Kit.</Typography>
                  <Button variant="contained" size="large" onClick={() => setActiveStep(1)}>Jetzt starten</Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        );
      }

      case "nameTagline": {
        const show = !!showErrors.nameTagline;
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <StepNotes stepKey={s.key} locked={locked} />
            <Stack spacing={3} sx={lockedContentSx}>
              <Field
                title="Name"
                description="Künstlername"
                required
                locked={locked}
              >
                <TextField
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={DEFAULT_NAME}
                  inputProps={{ maxLength: 48 }}
                  error={show && !name.trim()}
                  helperText={show && !name.trim() ? "Pflichtfeld" : `${name.length}/48`}
                />
              </Field>

              <Field
                title="Tagline"
                description="Was ist deine Besonderheit und was bietest du an?"
                required
                locked={locked}
              >
                <InputWithCounter
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  maxLength={70}
                  placeholder={DEFAULT_TAGLINE}
                  error={show && !tagline.trim()}
                />
                {show && !tagline.trim() && (
                  <Typography variant="caption" color="error">
                    Pflichtfeld
                  </Typography>
                )}
              </Field>

            </Stack>
          </Box>
        );
      }

      case "bio": {
        const show = !!showErrors.bio;
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <StepNotes stepKey={s.key} locked={locked} />
            <Stack spacing={3} sx={lockedContentSx}>

              <Field
                title="Bio"
                description="Wer bist du, wie klingst du, was passiert aktuell?"
                required
                locked={locked}
                action={
                  <Tooltip title="Auf ursprünglichen Text zurücksetzen">
                    <IconButton size="small" onClick={() => setBioBlock(DEFAULT_BIO)}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <MentionsTextarea
                  value={bioBlock}
                  onChange={setBioBlock}
                  maxLength={2100}
                  error={show && (!bioBlock.trim() || hasPlaceholders(bioBlock))}
                  helper={
                    show && hasPlaceholders(bioBlock)
                      ? "Bitte Platzhalter ersetzen."
                      : "Platzhalter in geschweiften Klammern kannst du direkt überschreiben."
                  }
                  data={BIO_MENTION_DATA}
                  locked={locked}
                />

                {show && !bioBlock.trim() && (
                  <Typography variant="caption" color="error">
                    Pflichtfeld
                  </Typography>
                )}
              </Field>
            </Stack>
          </Box>
        );
      }

      case "repertoire": {
        const show = !!showErrors.repertoire;
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <StepNotes stepKey={s.key} locked={locked} />
            <Box>
              <Stack spacing={3} sx={lockedContentSx}>

              <Field
                title="Angebot"
                description="Beschreibe in einem Fließtext, was du anbietest (z. B. Sets, Dauer, Formate)."
                locked={locked}
              >
                <TextareaWithCounter
                  value={angebotBlock}
                  onChange={(e) => setAngebotBlock(e.target.value)}
                  rows={6}
                  maxLength={1000}
                  placeholder={DEFAULT_ANGEBOT}
                />
              </Field>


              <Field
                id="field-repertoire"
                title="Repertoire"
                description="Liste wichtiger Songs/Setlist, kommasepariert. Beispiel: Shape of You - Ed Sheeran, Songtitel - Interpret."
                required
                locked={locked}
                action={
                  <Tooltip title="Auf ursprünglichen Text zurücksetzen">
                    <IconButton size="small" onClick={() => setRepertoireBlock(DEFAULT_REPERTOIRE)}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <TextareaWithCounter
                  value={repertoireBlock}
                  onChange={(e) => setRepertoireBlock(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder={DEFAULT_REPERTOIRE}
                  error={show && !repertoireBlock.trim()}
                />
                {!!repertoireItems.length && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 1 }}>
                    {repertoireItems.map((item, index) => (
                      <Chip
                        key={`${item}-${index}`}
                        label={item}
                        size="small"
                        color={item.length > REPERTOIRE_BUBBLE_WARNING_LENGTH ? "error" : "default"}
                      />
                    ))}
                  </Stack>
                )}
                {hasLongRepertoireItem && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Trenne Bubbles mit Komma.
                  </Typography>
                )}
                {show && !repertoireBlock.trim() && (
                  <Typography variant="caption" color="error">
                    Pflichtfeld
                  </Typography>
                )}
              </Field>

              </Stack>
            </Box>
          </Box>
        );
      }


      case "photos": {
        const show = !!showErrors.photos;
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <StepNotes stepKey={s.key} locked={locked} />
            <Box sx={lockedContentSx}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                <Field title="Header Foto (1:1)" description="Lade ein quadratisches Titelbild hoch und schneide es zu." required>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" component="label">Header‑Foto hochladen<input type="file" accept="image/*" hidden onChange={onHeroFile} /></Button>
                    {photoUrl && <Chip label="Header‑Foto bereit" color="success" icon={<CheckCircleIcon />} />}
                  </Stack>
                  {show && !photoUrl && <Typography variant="caption" color="error" sx={{ mt: 0.75 }}>Pflichtfeld</Typography>}
                  {photoUrl && (
                    <Box sx={{ maxWidth: 320, mt: 2 }}>
                      <img
                        src={photoUrl}
                        alt="Header‑Vorschau"
                        style={{ width: "100%", borderRadius: 8, display: "block", objectFit: "cover", height: "auto" }}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" onClick={() => setCropOpen(true)}>Zuschneiden</Button>
                      </Box>
                    </Box>
                  )}
                </Field>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                <Field title="Portrait (3:4)" description="Lade ein Hochformat‑Portrait hoch und schneide es zu." required>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" component="label">Portrait hochladen<input type="file" accept="image/*" hidden onChange={onPortraitFile} /></Button>
                    {portraitPhotoUrl && <Chip label="Portrait bereit" color="success" icon={<CheckCircleIcon />} />}
                  </Stack>
                  {show && !portraitPhotoUrl && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.75 }}>Pflichtfeld</Typography>
                  )}
                  {portraitPhotoUrl && (
                    <Box sx={{ maxWidth: 320, mt: 2 }}>
                      <img
                        src={portraitPhotoUrl}
                        alt="Portrait‑Vorschau"
                        style={{ width: "100%", borderRadius: 8, display: "block", objectFit: "cover", height: "auto" }}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" onClick={() => setPortraitCropOpen(true)}>Zuschneiden</Button>
                      </Box>
                    </Box>
                  )}
                </Field>
                </Grid>
              </Grid>

              <Stack spacing={3} sx={{ mt: 3 }}>
                <Field title="Pressematerial Link" description="Link zu deinem Pressematerial (z. B. Dropbox/Drive)." locked={locked}>
                  <TextField
                    fullWidth
                    value={pressMaterialUrl}
                    onChange={(e) => setPressMaterialUrl(e.target.value)}
                    placeholder={DEFAULT_PRESS_MATERIAL_URL}
                  />
                </Field>
                <Field title="Pressematerial Text" description="Kurze Beschreibung oder Hinweise zu deinem Pressematerial." locked={locked}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={5}
                    value={pressMaterialText}
                    inputProps={{ maxLength: 400 }}
                    onChange={(e) => {
                      // Maximal 5 Zeilen zulassen (höchstens 4 Zeilenumbrüche).
                      if ((e.target.value.match(/\n/g) || []).length <= 4) {
                        setPressMaterialText(e.target.value);
                      }
                    }}
                    helperText={`${pressMaterialText.length}/400`}
                    placeholder={DEFAULT_PRESS_MATERIAL_TEXT}
                  />
                </Field>
              </Stack>
            </Box>
          </Box>
        );
      }

      case "rider": {
        const show = !!showErrors.rider;
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <StepNotes stepKey={s.key} locked={locked} />
            <Box>
              <Stack spacing={3} sx={lockedContentSx}>
              <Field
                title="Tech Rider"
                description="Bühne, Strom, Monitoring, Backline, Aufbau/Abbau. Platzhalter in geschweiften Klammern kannst du überschreiben."
                required
                locked={locked}
                action={
                  <Tooltip title="Auf ursprünglichen Text zurücksetzen">
                    <IconButton size="small" onClick={() => setRiderBlock(DEFAULT_RIDER)}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <MentionsTextarea
                  value={riderBlock}
                  onChange={setRiderBlock}
                  maxLength={2500}
                  error={show && (!riderBlock.trim() || hasPlaceholders(riderBlock))}
                  helper={
                    show && hasPlaceholders(riderBlock)
                      ? "Bitte Platzhalter ersetzen."
                      : "Platzhalter kannst du direkt im Text überschreiben."
                  }
                  data={RIDER_MENTION_DATA}
                  locked={locked}
                />

                {show && !riderBlock.trim() && (
                  <Typography variant="caption" color="error">
                    Pflichtfeld
                  </Typography>
                )}
              </Field>
              </Stack>
            </Box>
          </Box>
        );
      }

      case "contactCta": {
        const show = !!showErrors.contactCta;
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <StepNotes stepKey={s.key} locked={locked} />
            <Box>
              <Stack spacing={3} sx={lockedContentSx}>
              <Field title="E-Mail" description="Deine Kontakt‑E-Mail." required locked={locked}>
                <TextField
                  fullWidth
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder={DEFAULT_CONTACT_EMAIL_HINT}
                  error={show && !contactEmail.trim()}
                  helperText={show && !contactEmail.trim() ? "Pflichtfeld" : ""}
                />
              </Field>

              <Field title="Telefonnummer" description="Deine Telefonnummer." required locked={locked}>
                <TextField
                  fullWidth
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder={DEFAULT_CONTACT_PHONE_HINT}
                  error={show && !contactPhone.trim()}
                  helperText={show && !contactPhone.trim() ? "Pflichtfeld" : ""}
                />
              </Field>

              <Field
                title="Social Tags"
                description="Instagram, TikTok, YouTube etc. – jeweils in einer neuen Zeile."
                required
                locked={locked}
                action={
                  <Tooltip title="Auf ursprünglichen Text zurücksetzen">
                    <IconButton size="small" onClick={() => setContact(DEFAULT_SOCIAL_TAGS)}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <MentionsTextarea
                  value={contact}
                  onChange={setContact}
                  maxLength={300}
                  error={show && (!contact.trim() || hasPlaceholders(contact))}
                  helper={
                    show && hasPlaceholders(contact)
                      ? "Bitte Platzhalter ersetzen."
                      : "Platzhalter kannst du direkt im Text überschreiben."
                  }
                  data={SOCIAL_TAGS_MENTION_DATA}
                  locked={locked}
                />
                {show && !contact.trim() && (
                  <Typography variant="caption" color="error">
                    Pflichtfeld
                  </Typography>
                )}
              </Field>

              <Field
                title="Kontakt Untertitel"
                description="Kurzer Satz unter der Kontakt-Überschrift."
                locked={locked}
              >
                <TextField
                  fullWidth
                  value={contactSubtitle}
                  onChange={(e) => setContactSubtitle(e.target.value)}
                  placeholder={DEFAULT_CONTACT_SUBTITLE}
                  inputProps={{ maxLength: 140 }}
                />
              </Field>

              <Field locked={locked} title="CTA URL" description="Vollständige URL, wohin der Button führen soll." required>
                <TextField
                  fullWidth
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  onFocus={() => {
                    if (ctaUrl.trim() === DEFAULT_CTA_URL) {
                      setCtaUrl("");
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.value.trim()) {
                      setCtaUrl(DEFAULT_CTA_URL);
                    }
                  }}
                  error={show && !ctaUrl.trim()}
                  helperText={show && !ctaUrl.trim() ? "Pflichtfeld" : ""}
                />
              </Field>

              <Field locked={locked} title="CTA Label" description="Beschreibe die Aktion, z. B. 'Jetzt ansehen'." required>
                <TextField
                  fullWidth
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  inputProps={{ maxLength: 28 }}
                  error={show && !ctaLabel.trim()}
                  helperText={show && !ctaLabel.trim() ? "Pflichtfeld" : `${ctaLabel.length}/28`}
                />
              </Field>

              <Field
                title="CTA Beschreibung"
                description="2–3 Sätze die Beschreiben, was sich hinter deinem CTA verbirgt"
                required
                locked={locked}
                action={
                  <Tooltip title="Auf ursprünglichen Text zurücksetzen">
                    <IconButton
                      size="small"
                      onClick={() => setHeroText(DEFAULT_HEROTEXT)}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <MentionsTextarea
                  value={heroText}
                  onChange={setHeroText}
                  maxLength={250}
                  error={show && (!heroText.trim() || hasPlaceholders(heroText))}
                  helper={
                    show && hasPlaceholders(heroText)
                      ? "Bitte alle Platzhalter-Tags ersetzen."
                      : "Tags anklicken, um sie zu überschreiben oder zu löschen."
                  }
                  data={HERO_MENTION_DATA}   // <- aus DEFAULT_HEROTEXT generiert
                  locked={locked}
                />
                {show && !heroText.trim() && (
                  <Typography variant="caption" color="error">
                    Pflichtfeld
                  </Typography>
                )}
              </Field>

              <Field
                title="Social Proof"
                description="Ein kurzer einzeiliger Vertrauensbeweis, steht bei „Bekannt aus“"
                locked={locked}
              >
                <InputWithCounter
                  value={ctaSocialProof}
                  onChange={(e) => setCtaSocialProof(e.target.value)}
                  maxLength={200}
                  placeholder="Bezirkszeitung, Radio Wien, FM4, …"
                />
              </Field>

              </Stack>
            </Box>
          </Box>
        );
      }
      case "design": {
        return (
          <Box>
            <StepVideo src={currentVideoSrc} locked={locked} />
            <Box>
              <Stack spacing={2} alignItems="center">
                <Button variant="contained" size="large" onClick={() => setDesignEditorOpen(true)}>
                  Jetzt Mediakit finalisieren
                </Button>
              </Stack>
            </Box>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  // ===== App Bar (link/rechts minimal), Burger leicht größer =====
  return (
    <ThemeProvider theme={theme}>
      <GeneratorHeader
        steps={steps}
        activeStep={activeStep}
        canClickStep={canClickStep}
        isStepComplete={isStepComplete}
        onStepClick={handleStepClick}
        onOpenMenu={openMenu}
        onOpenSidebar={() => setSidebarOpen(true)}
        isSaving={isSaving}
        lastSavedAt={lastSavedAt}
        formatTimeHHMM={formatTimeHHMM}
        onOpenDesignEditor={() => setDesignEditorOpen(true)}
        NumberStepIcon={NumberStepIcon}
        accentColor={PURPLE}
      />

      {/* Sidebar ohne „fertig“-Tags */}
      <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <Box role="presentation" sx={{ width: 320 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1, color: "text.secondary" }}>
              Kapitel
            </Typography>
          </Box>
          <Divider />
          <List>
            {steps.map((s, idx) => {
              const active = idx === activeStep;
              return (
                <ListItem key={s.key} disablePadding>
                  <ListItemButton
                    selected={active}
                    disabled={!canClickStep(idx)}
                    onClick={() => {
                      if (!canClickStep(idx)) return;
                      handleStepClick(idx);
                      setSidebarOpen(false);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <NumberStepIcon
                        active={active}
                        complete={isStepComplete(idx)}
                        icon={idx + 1}
                        size={18}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={s.title}
                      secondary={s.desc}
                      primaryTypographyProps={{ fontWeight: active ? 700 : 500 }}
                      secondaryTypographyProps={{ sx: { whiteSpace: "normal" } }}
                    />
                  </ListItemButton>
                </ListItem>

              );
            })}
          </List>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Brauchst du Hilfe?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Schreib uns jederzeit, wir helfen dir schnell weiter.
            </Typography>
            <Button
              component="a"
              href="mailto:office@showkompass.at"
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", borderRadius: 999, fontWeight: 700 }}
            >
              Support kontaktieren
            </Button>
          </Box>
        </Box>
      </Drawer>

      <DesignEditor
        open={designEditorOpen}
        onClose={() => setDesignEditorOpen(false)}
        previewPages={previewPages}
        previewIndex={previewIndex}
        setPreviewIndex={setPreviewIndex}
        previewPanePct={previewPanePct}
        setPreviewPanePct={setPreviewPanePct}
        previewError={previewError}
        previewLoading={previewLoading}
        pdfLoading={busy}
        onDownloadPdf={handleDownloadPdf}
        onRefreshPreview={handleRefreshPreviewClick}
        onFillLorem={handleFillLorem}
        onFillMinimum={handleFillMinimum}
        onFillMaximum={handleFillMaximum}
        onPremiumClick={() => handlePremiumClick("design_editor")}
        isPro={isPro}
        designId={designId}
        setDesignId={handleDesignTemplateChange}
        designTemplates={visibleDesignTemplates}
        colorTemplates={visibleColorTemplates}
        isCustomActive={isCustomActive}
        setUseCustomColors={setUseCustomColors}
        customColors={customColors}
        setCustomColors={setCustomColors}
        templateId={templateId}
        setTemplateId={setTemplateId}
        fontId={fontId}
        setFontId={setFontId}
        fontOptions={FONT_OPTIONS}
        bodyFontOptions={BODY_FONT_OPTIONS}
        nameFontId={nameFontId}
        setNameFontId={setNameFontId}
        nameFontSize={nameFontSize}
        setNameFontSize={setNameFontSize}
        nameColorKey={nameColorKey}
        setNameColorKey={setNameColorKey}
        nameOutlineEnabled={nameOutlineEnabled}
        setNameOutlineEnabled={setNameOutlineEnabled}
        nameOutlineColorKey={nameOutlineColorKey}
        setNameOutlineColorKey={setNameOutlineColorKey}
        titleCtaLinkEnabled={titleCtaLinkEnabled}
        setTitleCtaLinkEnabled={setTitleCtaLinkEnabled}
        titleCtaQrEnabled={titleCtaQrEnabled}
        setTitleCtaQrEnabled={setTitleCtaQrEnabled}
        titleFontId={titleFontId}
        setTitleFontId={setTitleFontId}
        titleFontSize={titleFontSize}
        setTitleFontSize={setTitleFontSize}
        bodyFontSize={bodyFontSize}
        setBodyFontSize={setBodyFontSize}
        bodySizeOptions={BODY_SIZE_OPTIONS}
        bodySizeDisabledValues={bioFormatDisabled.sizeValues}
        lineSpacingDisabledIds={bioFormatDisabled.spacingIds}
        heroOffset={heroOffsets[designId] || { x: 50, y: 50 }}
        setHeroOffset={(off) => setHeroOffsets((prev) => ({ ...prev, [designId]: off }))}
        portraitOffset={portraitOffset}
        setPortraitOffset={setPortraitOffset}
        onOpenHeroCrop={() => setCropOpen(true)}
        onOpenPortraitCrop={() => setPortraitCropOpen(true)}
        hasHeroPhoto={!!(rawPhotoUrl || photoUrl)}
        hasPortraitPhoto={!!(rawPortraitPhotoUrl || portraitPhotoUrl)}
        lineSpacing={lineSpacing}
        setLineSpacing={setLineSpacing}
        lineSpacingOptions={LINE_SPACING_OPTIONS}
        bioImageScale={bioImageScale}
        setBioImageScale={setBioImageScale}
        elementRoundness={elementRoundness}
        setElementRoundness={setElementRoundness}
        dividerStyle={dividerStyle}
        setDividerStyle={setDividerStyle}
        dividerConfig={dividerConfig}
        setDividerConfig={setDividerConfig}
        dividerTypeOptions={DIVIDER_TYPE_OPTIONS}
        dividerSpacingStep={DIVIDER_SPACING_STEP}
        footerEnabled={footerEnabled}
        setFooterEnabled={setFooterEnabled}
        sectionLabels={SECTION_LABELS}
        sectionModes={sectionModes}
        setSectionModes={setSectionModes}
        sectionVisibility={sectionVisibility}
        setSectionVisibility={setSectionVisibility}
        sectionColorModes={SECTION_COLOR_MODES}
        sectionOrder={normalizeSectionOrder(sectionOrder)}
        setSectionOrder={setSectionOrder}
        sectionPadding={sectionPadding}
        setSectionPadding={setSectionPadding}
        titleColorKey={titleColorKey}
        setTitleColorKey={setTitleColorKey}
        bodyColorKey={bodyColorKey}
        setBodyColorKey={setBodyColorKey}
        sectionGeometry={sectionGeometry}
        previewPalette={previewPalette}
        accentColor={PURPLE}
      />

      <Box
        sx={{
          background:
            "radial-gradient(1100px 520px at 12% 0%, rgba(163,216,30,0.08), transparent 56%), radial-gradient(900px 500px at 90% 18%, rgba(34,211,238,0.10), transparent 56%)",
          py: 3,
        }}
      >
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            {renderStep(activeStep)}
            {activeStep === 0 && (
              <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.6,
                    borderRadius: 2.5,
                    border: "1px solid rgba(15,23,42,0.08)",
                    bgcolor: "rgba(255,255,255,0.85)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                  }}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Hinweis: Für die beste Erfahrung empfehlen wir die Nutzung am PC.
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {!isPro && isLocked(activeStep) && (
        <Container maxWidth="md" sx={{ pb: 2 }}>
          <Box
            sx={{
              position: "relative",
              px: { xs: 2, sm: 3.5 },
              py: { xs: 2.5, sm: 3.2 },
              borderRadius: 4,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              alignItems: "center",
              gap: { xs: 1.6, sm: 3 },
              background:
                "linear-gradient(135deg, rgba(125,156,20,0.20), rgba(195,240,76,0.22))",
              border: "1px solid rgba(125,156,20,0.32)",
              boxShadow: "0 20px 60px rgba(23,23,15,0.22)",
              backdropFilter: "blur(12px)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -80,
                background:
                  "radial-gradient(420px 220px at 10% 10%, rgba(23,23,15,0.18), transparent 60%), radial-gradient(420px 240px at 90% 10%, rgba(163,216,30,0.22), transparent 60%)",
                pointerEvents: "none",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                  Premium
                </Typography>
                {badge ? (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.4,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 800,
                      bgcolor: "rgba(15,23,42,0.15)",
                      color: "#0f172a",
                    }}
                  >
                    {badge}
                  </Box>
                ) : null}
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.5 }}>
                Vollen Zugriff zum Generator
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.7,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                PDFs für 2 Jahre | 8 Video-Einheiten | Premium Support
              </Typography>
            </Box>
            <Stack spacing={0.6} alignItems="flex-end">
              <Button
                variant="contained"
                onClick={() => handlePremiumClick("premium_banner")}
                sx={{
                  borderRadius: 999,
                  fontWeight: 800,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  px: 3.2,
                  color: "#14150c",
                  background: "linear-gradient(135deg, #c3f04c 0%, #a3d81e 100%)",
                  boxShadow: "0 14px 32px rgba(23,23,15,0.35)",
                }}
              >
                Vollversion sichern
              </Button>
            </Stack>
          </Box>
        </Container>
      )}

      <Container maxWidth="md" sx={{ pb: 4, pt: 0 }}>
        <Stack spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Wie hilfreich war dieser Schritt?
          </Typography>
          <Rating
            value={stepRatings[activeStep] || 0}
            onChange={(_, value) => handleStepRating(value)}
          />
        </Stack>
      </Container>

      {activeStep > 0 && (
        <Box sx={{ position: "fixed", left: { xs: 12, sm: 24 }, bottom: { xs: 16, sm: 24 }, zIndex: 1200 }}>
          <Button
            variant="outlined"
            onClick={goBack}
            sx={{ borderRadius: 999, px: 2.5, py: 1 }}
          >
            Zurück
          </Button>
        </Box>
      )}
      {activeStep > 0 && activeStep < steps.length - 1 && (
        <Box sx={{ position: "fixed", right: { xs: 12, sm: 24 }, bottom: { xs: 16, sm: 24 }, zIndex: 1200 }}>
          <Button
            variant="contained"
            endIcon={activeStep >= steps.length - 1 ? undefined : <ArrowForwardIosIcon />}
            onClick={goNext}
            sx={{ borderRadius: 999, px: 2.5, py: 1 }}
          >
            {activeStep >= steps.length - 1 ? "Designeditor öffnen" : "Weiter"}
          </Button>
        </Box>
      )}

      <Dialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Feedback</DialogTitle>
        <DialogContent dividers>
          <PosthogFeedbackTool
            meta={{
              step: steps[activeStep]?.key,
              isPro,
              designId,
              palette: useCustomColors ? "custom" : (templateId || ""),
              fontId,
              sectionModes,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={exportIncompleteOpen}
        onClose={() => setExportIncompleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Formularfelder noch unvollständig</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Nicht alle Pflichtfelder sind ausgefüllt oder enthalten Platzhalter. Du kannst
            jetzt zurückgehen und die fehlenden Angaben ergänzen oder trotzdem exportieren.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportIncompleteOpen(false)}>Zurück</Button>
          <Button
            variant="contained"
            onClick={() => {
              setExportIncompleteOpen(false);
              onGenerate(false, true);
            }}
          >
            Trotzdem exportieren
          </Button>
        </DialogActions>
      </Dialog>


      {/* Overflow menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={handleDownloadJson}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Formular herunterladen</ListItemText>
        </MenuItem>
        <MenuItem onClick={triggerUpload}>
          <ListItemIcon>
            <UploadFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Formular hochladen</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleResetAll}>
          <ListItemIcon>
            <RestartAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Formular zurücksetzen</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem component="a" href="/" onClick={closeMenu}>
          <ListItemIcon>
            <ArrowBackIosNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Zur Website</ListItemText>
        </MenuItem>
        <MenuItem component="a" href="/privacy" onClick={closeMenu}>
          <ListItemIcon>
            <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Impressum</ListItemText>
        </MenuItem>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {premiumStatusText}
          </Typography>
        </Box>
      </Menu>

      <input
        ref={uploadInputRef}
        type="file"
        accept=".mkit"
        style={{ display: "none" }}
        onChange={handleUploadFile}
      />

      {/* HERO crop dialog */}
      <Dialog open={cropOpen} onClose={() => setCropOpen(false)} fullWidth maxWidth="sm" sx={{ zIndex: 2000 }}>
        <DialogTitle>Header-Foto zuschneiden</DialogTitle>
        <DialogContent>
          <Box
            ref={cropContainerRef}
            sx={{ position: "relative", width: "100%", height: 360, bgcolor: "#111", borderRadius: 1, overflow: "hidden" }}
          >
            {(rawPhotoUrl || photoUrl) && (
              <Cropper
                image={rawPhotoUrl || photoUrl}
                crop={crop}
                zoom={zoom}
                aspect={heroFrameAspect || HERO_ASPECT}
                cropSize={heroCropSize || undefined}
                objectFit="contain"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, cp) => setCroppedPixels(cp)}
                restrictPosition
                showGrid={false}
              />
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ width: "100%" }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={confirmHeroCrop}>Übernehmen</Button>
        </DialogActions>
      </Dialog>

      {/* PORTRAIT crop dialog */}
      <Dialog open={portraitCropOpen} onClose={() => setPortraitCropOpen(false)} fullWidth maxWidth="sm" sx={{ zIndex: 2000 }}>
        <DialogTitle>Portrait zuschneiden</DialogTitle>
        <DialogContent>
          <Box sx={{ position: "relative", width: "100%", height: 400, bgcolor: "#111", borderRadius: 1, overflow: "hidden" }}>
            {(rawPortraitPhotoUrl || portraitPhotoUrl) && (
              <Cropper
                image={rawPortraitPhotoUrl || portraitPhotoUrl}
                crop={portraitCrop}
                zoom={portraitZoom}
                aspect={PORTRAIT_ASPECT}
                onCropChange={setPortraitCrop}
                onZoomChange={setPortraitZoom}
                onCropComplete={(_, cp) => setPortraitCroppedPixels(cp)}
                restrictPosition
                showGrid={false}
              />
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <input type="range" min={1} max={3} step={0.01} value={portraitZoom} onChange={(e) => setPortraitZoom(Number(e.target.value))} style={{ width: "100%" }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortraitCropOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={confirmPortraitCrop}>Übernehmen</Button>
        </DialogActions>
      </Dialog>

      <PurchaseSuccessDialog
        open={purchaseSuccessOpen}
        onClose={() => setPurchaseSuccessOpen(false)}
        token={purchaseToken || localStorage.getItem(PRO_KEY) || ""}
      />
      <TokenExpiredDialog
        open={expiredTokenOpen}
        onClose={() => setExpiredTokenOpen(false)}
      />

    </ThemeProvider>
  );
}
